import { useDispatch, useSelector } from 'react-redux';
import {
  ActionType,
  CauseType,
  ConsequenceType,
  DeviationType,
  MethodologyLabels,
  MethodologyType,
  MatrixRiskType,
  MatrixType,
} from '../../../../types/types';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NotificationContext } from '../../../../providers/NotificationProvider';
import {
  getLikelihoodLevelsByMatrixId,
  getRiskLevelsByMatrixId,
  getSeverityLevelsByMatrixId,
} from '../../../../features/projects/matrixSlices';
import {
  assignCauseToDeviation,
  assignConsequenceToCause,
  createAction,
  CreateActionType,
  createCause,
  CreateCauseType,
  createConsequence,
  CreateConsequenceType,
  createSafeguard,
  CreateSafeguardType,
  getDeviation,
  cloneCause,
  assignActionToConsequence,
  getProjectCausesByProjectId,
  getProjectActionsByProjectId,
  getProjectConsequencesByProjectId,
  getSafeguardNamesByProjectId,
  copyConsequenceToCause,
  moveActionDown,
  moveActionUp,
  moveCauseDown,
  moveCauseUp,
  moveSafeguardDown,
  moveSafeguardUp,
  updateConsequence,
  UpdateConsequenceType,
} from '../../../../features/projects/deviationSlice';
import { AutoComplete, Button, Select, Table, Tag, Tooltip, Typography } from 'antd';
import {
  LinkOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  PlusCircleOutlined,
  CopyOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import ConsequenceRow from './ConsequenceRow';
import SafeguardRow from './SafeguardRow';
import ActionRow from './ActionRow';
import TextArea from 'antd/lib/input/TextArea';
import { RootState } from '../../../../store/store';
import { useTranslation } from 'react-i18next';

const normalizeForSearch = (text: string | null | undefined): string => {
  if (!text) {
    return '';
  }
  const stringValue = text.toString();
  const normalized = typeof stringValue.normalize === 'function' ? stringValue.normalize('NFD') : stringValue;
  return normalized
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const normalizeActionId = (value: unknown): number | null => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const parsed = Number(trimmed);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return null;
};

const toNumericConsequenceIds = (
  rawValues: Array<number | { id?: number | null } | null> | null | undefined
): number[] => {
  if (!Array.isArray(rawValues)) {
    return [];
  }
  return rawValues
    .map((value) => {
      if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
      }
      if (value && typeof value === 'object' && 'id' in value) {
        const candidate = (value as { id?: number | null }).id ?? null;
        if (typeof candidate === 'number' && !Number.isNaN(candidate)) {
          return candidate;
        }
      }
      return null;
    })
    .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id));
};

const resolveConsequenceIdFromAction = (
  action: Partial<ActionType> & { consequenceId?: number | null; consequence_id?: number | null }
): number | null => {
  if (typeof action?.consequenceId === 'number' && !Number.isNaN(action.consequenceId)) {
    return action.consequenceId;
  }
  if (typeof action?.consequence_id === 'number' && !Number.isNaN(action.consequence_id)) {
    return action.consequence_id;
  }
  const [firstRelation] = toNumericConsequenceIds(action?.consequences ?? []);
  return typeof firstRelation === 'number' && !Number.isNaN(firstRelation) ? firstRelation : null;
};

const compareByOrderThenId = <T extends { id?: number | null; order?: number | null; project_order?: number | null }>(
  a: T,
  b: T,
  orderField: 'order' | 'project_order' = 'order'
): number => {
  const orderA =
    typeof a?.[orderField] === 'number' && !Number.isNaN(a[orderField] as number)
      ? (a[orderField] as number)
      : Number.MAX_SAFE_INTEGER;
  const orderB =
    typeof b?.[orderField] === 'number' && !Number.isNaN(b[orderField] as number)
      ? (b[orderField] as number)
      : Number.MAX_SAFE_INTEGER;
  if (orderA !== orderB) {
    return orderA - orderB;
  }
  const idA = typeof a?.id === 'number' && !Number.isNaN(a.id) ? a.id : Number.MAX_SAFE_INTEGER;
  const idB = typeof b?.id === 'number' && !Number.isNaN(b.id) ? b.id : Number.MAX_SAFE_INTEGER;
  return idA - idB;
};

const sharedEditWarningMessage = 'Editar afecta todas las relaciones. Usa clonar para independizar.';

const extractErrorMessage = (payload: unknown): string | null => {
  if (payload === null || payload === undefined) {
    return null;
  }
  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    return trimmed.length ? trimmed : null;
  }
  if (Array.isArray(payload)) {
    const messages = payload
      .map((item) => (typeof item === 'string' ? item.trim() : null))
      .filter((message): message is string => Boolean(message));
    return messages.length ? messages.join('\n') : null;
  }
  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    if ('error' in record) {
      const nested = extractErrorMessage(record.error);
      if (nested) {
        return nested;
      }
    }
    const nestedMessages = Object.values(record)
      .map((value) => extractErrorMessage(value))
      .filter((message): message is string => Boolean(message));
    if (nestedMessages.length) {
      return nestedMessages.join('\n');
    }
    return null;
  }
  if (typeof payload === 'number' || typeof payload === 'boolean') {
    return String(payload);
  }
  return null;
};

type DeviationTableProps = {
  deviation: DeviationType;
  onEdit: (type: string, record: any) => void;
  onDelete: (type: string, record: any) => void;
  onAdd: (type: string) => void;
  editingItem: { type: string; id: number | null };
  setEditingItem: any;
  editedFormValues: any;
  setEditedFormValues: any;
  modifyCause: any;
  modifyConsequence: any;
  modifySafeguard?: any;
  modifyAction?: (actionId?: number | null) => void;
  matricesAvailable?: MatrixType[];
  labels?: MethodologyLabels;
  methodology?: MethodologyType | null;
  onNavigateToDeviation?: (deviationId: number) => void;
};

const DeviationTable: React.FC<DeviationTableProps> = ({
  deviation,
  onEdit,
  onDelete,
  onAdd,
  editingItem,
  setEditingItem,
  editedFormValues,
  setEditedFormValues,
  modifyCause,
  modifyConsequence,
  modifySafeguard,
  modifyAction,
  matricesAvailable = [],
  labels,
  methodology,
  onNavigateToDeviation,
}) => {
  const { t } = useTranslation();
  const { Text } = Typography;
  const renderSharedEditWarning = () => (
    <Tooltip title={t(sharedEditWarningMessage)} trigger={['hover', 'focus']} destroyTooltipOnHide mouseEnterDelay={0.05}>
      <InfoCircleOutlined
        style={{
          color: '#faad14',
          fontSize: 14,
          cursor: 'help',
        }}
      />
    </Tooltip>
  );
  const causesLabel = labels?.causes ?? t('Causas');
  const consequencesLabel = labels?.consequences ?? t('Consecuencias');
  const safeguardsLabel = labels?.safeguards ?? t('Salvaguardas');
  const recommendationsLabel = labels?.recommendations ?? t('Recomendaciones');
  const showRiskAnalysis = methodology?.show_risk_analysis ?? true;
  const showSafeguards = methodology?.show_safeguards ?? true;
  const showRecommendations = methodology?.show_recommendations ?? true;
  const { triggerNotification } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const projectId = typeof deviation?.project === 'number' ? deviation.project : null;
  const safeguardNamesByProject = useSelector((state: RootState) => state.deviation.safeguardNamesByProject);
  const loadingSafeguardNames = useSelector((state: RootState) => state.deviation.loadingSafeguardNames);
  const safeguardNamesLoadedMap = useSelector((state: RootState) => state.deviation.safeguardNamesLoaded);
  const projectActionsByProject = useSelector((state: RootState) => state.deviation.projectActionsByProject);
  const loadingProjectActions = useSelector((state: RootState) => state.deviation.loadingProjectActions);
  const projectActionsLoadedMap = useSelector((state: RootState) => state.deviation.projectActionsLoaded);
  const projectCausesByProject = useSelector((state: RootState) => state.deviation.projectCausesByProject);
  const loadingProjectCauses = useSelector((state: RootState) => state.deviation.loadingProjectCauses);
  const projectCausesLoadedMap = useSelector((state: RootState) => state.deviation.projectCausesLoaded);
  const projectConsequencesByProject = useSelector((state: RootState) => state.deviation.projectConsequencesByProject);
  const loadingProjectConsequences = useSelector((state: RootState) => state.deviation.loadingProjectConsequences);
  const projectConsequencesLoadedMap = useSelector((state: RootState) => state.deviation.projectConsequencesLoaded);
  const safeguardNamesForProject: string[] = useMemo(() => {
    if (!projectId) {
      return [];
    }
    return safeguardNamesByProject[projectId] ?? [];
  }, [projectId, safeguardNamesByProject]);
  const isLoadingSafeguardNames = projectId ? loadingSafeguardNames[projectId] ?? false : false;
  const hasLoadedSafeguardNames = projectId ? safeguardNamesLoadedMap[projectId] ?? false : false;
  const projectActionsForProject: ActionType[] = useMemo(() => {
    if (!projectId) {
      return [];
    }
    return projectActionsByProject[projectId] ?? [];
  }, [projectId, projectActionsByProject]);
  const isLoadingProjectActions = projectId ? loadingProjectActions[projectId] ?? false : false;
  const hasLoadedProjectActions = projectId ? projectActionsLoadedMap[projectId] ?? false : false;
  const projectCausesForProject: CauseType[] = useMemo(() => {
    if (!projectId) {
      return [];
    }
    return projectCausesByProject[projectId] ?? [];
  }, [projectId, projectCausesByProject]);
  const isLoadingProjectCauses = projectId ? loadingProjectCauses[projectId] ?? false : false;
  const hasLoadedProjectCauses = projectId ? projectCausesLoadedMap[projectId] ?? false : false;
  const projectConsequencesForProject: ConsequenceType[] = useMemo(() => {
    if (!projectId) {
      return [];
    }
    return projectConsequencesByProject[projectId] ?? [];
  }, [projectId, projectConsequencesByProject]);
  const isLoadingProjectConsequences = projectId ? loadingProjectConsequences[projectId] ?? false : false;
  const hasLoadedProjectConsequences = projectId ? projectConsequencesLoadedMap[projectId] ?? false : false;
  const safeguardAutoCompleteOptions = useMemo(
    () => safeguardNamesForProject.map((name) => ({ value: name, label: name })),
    [safeguardNamesForProject]
  );
  const currentDeviationConsequenceIds = useMemo(() => {
    const consequences = Array.isArray(deviation?.consequences) ? deviation.consequences : [];
    return consequences
      .map((item) => {
        const id = item?.id;
        return typeof id === 'number' && !Number.isNaN(id) ? id : null;
      })
      .filter((id): id is number => id !== null);
  }, [deviation?.consequences]);
  const currentDeviationConsequenceIdSet = useMemo(
    () => new Set<number>(currentDeviationConsequenceIds),
    [currentDeviationConsequenceIds]
  );

  const allProjectActions: ActionType[] = useMemo(() => {
    const actionsMap = new Map<string, ActionType>();

    const getKey = (action: ActionType) => {
      const normalizedId = normalizeActionId(action?.id);
      if (normalizedId !== null) {
        return `id-${normalizedId}`;
      }
      return `name-${normalizeForSearch(action.name ?? '')}`;
    };

    const mergeActions = (existing: ActionType, incoming: ActionType): ActionType => {
      const merged: ActionType = { ...existing };
      const preferIncomingString = <K extends keyof ActionType>(key: K) => {
        const currentValue = existing[key];
        const nextValue = incoming[key];
        const currentString = typeof currentValue === 'string' ? currentValue.trim() : currentValue;
        const nextString = typeof nextValue === 'string' ? nextValue.trim() : nextValue;
        const hasNext = nextString !== undefined && nextString !== null && nextString !== '';
        if (hasNext) {
          merged[key] = nextValue as any;
          return;
        }
        if (currentString !== undefined && currentString !== null && currentString !== '') {
          merged[key] = currentValue as any;
        }
      };
      preferIncomingString('name');
      preferIncomingString('responsability');
      preferIncomingString('asociated_risk');

      const currentOrder =
        typeof existing.project_order === 'number' && !Number.isNaN(existing.project_order)
          ? existing.project_order
          : null;
      const nextOrder =
        typeof incoming.project_order === 'number' && !Number.isNaN(incoming.project_order)
          ? incoming.project_order
          : null;
      if (nextOrder !== null && (currentOrder === null || nextOrder < currentOrder)) {
        merged.project_order = nextOrder;
      }

      const mergeArrayField = <K extends 'consequences' | 'causes'>(key: K) => {
        const currentValue = existing[key];
        const nextValue = incoming[key];
        const currentArray = Array.isArray(currentValue) ? currentValue : [];
        const nextArray = Array.isArray(nextValue) ? nextValue : [];
        if (nextArray.length) {
          const combined = [...currentArray, ...nextArray];
          const uniqueMap = new Map<string, any>();
          combined.forEach((item, idx) => {
            let keyValue: string;
            if (typeof item === 'number') {
              keyValue = `number-${item}`;
            } else if (item && typeof item === 'object' && 'id' in item && (item as any).id !== undefined) {
              keyValue = `object-${(item as any).id}`;
            } else {
              keyValue = `idx-${idx}`;
            }
            if (!uniqueMap.has(keyValue)) {
              uniqueMap.set(keyValue, item);
            }
          });
          const unique = Array.from(uniqueMap.values());
          merged[key] = unique as any;
        }
      };
      mergeArrayField('consequences');
      mergeArrayField('causes');

      const existingId = normalizeActionId(existing.id);
      const incomingId = normalizeActionId(incoming.id);
      if (existingId !== null) {
        merged.id = existingId;
      } else if (incomingId !== null) {
        merged.id = incomingId;
      } else {
        merged.id = null;
      }

      return merged;
    };

    const addAction = (action: ActionType | null | undefined) => {
      if (!action) return;
      const normalizedId = normalizeActionId(action?.id);
      const normalizedAction =
        normalizedId !== null
          ? action.id === normalizedId
            ? action
            : {
                ...action,
                id: normalizedId,
              }
          : action.id === null
          ? action
          : {
              ...action,
              id: null,
            };
      const key = getKey(normalizedAction);
      const existing = actionsMap.get(key);
      if (existing) {
        actionsMap.set(key, mergeActions(existing, normalizedAction));
      } else {
        actionsMap.set(key, normalizedAction);
      }
    };

    projectActionsForProject.forEach(addAction);

    const deviationActions = Array.isArray(deviation?.actions) ? deviation?.actions : [];
    deviationActions.forEach(addAction);

    const consequences = Array.isArray(deviation?.consequences) ? deviation?.consequences : [];
    consequences.forEach((consequence) => {
      const consequenceActions = Array.isArray(consequence?.actions) ? consequence?.actions : [];
      consequenceActions.forEach(addAction);
    });

    const causes = Array.isArray(deviation?.causes) ? deviation?.causes : [];
    causes.forEach((cause) => {
      const causeConsequences = Array.isArray(cause?.consequences) ? cause?.consequences : [];
      causeConsequences.forEach((consequence) => {
        const causeActions = Array.isArray(consequence?.actions) ? consequence?.actions : [];
        causeActions.forEach(addAction);
      });
    });

    const results = Array.from(actionsMap.values());

    const sorted = [...results].sort((a, b) => {
      const orderA =
        typeof a?.project_order === 'number' && !Number.isNaN(a.project_order)
          ? a.project_order
          : Number.MAX_SAFE_INTEGER;
      const orderB =
        typeof b?.project_order === 'number' && !Number.isNaN(b.project_order)
          ? b.project_order
          : Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      const idA = typeof a?.id === 'number' ? a.id : Number.MAX_SAFE_INTEGER;
      const idB = typeof b?.id === 'number' ? b.id : Number.MAX_SAFE_INTEGER;
      if (idA !== idB) {
        return idA - idB;
      }
      return normalizeForSearch(a?.name).localeCompare(normalizeForSearch(b?.name));
    });

    return sorted;
  }, [projectActionsForProject, deviation]);

  type ActionAutoCompleteOption = {
    key?: string;
    value: string;
    label: React.ReactNode;
    actionId: number | null;
    name: string;
    responsability: string;
    asociated_risk: string;
    project_order: number | null;
    searchValue: string;
    consequenceIds: number[];
  };
  const actionAutoCompleteOptions: ActionAutoCompleteOption[] = useMemo(() => {
    return allProjectActions.map((action, index) => {
      const orderPrefix =
        action?.project_order !== null && action?.project_order !== undefined ? `${action.project_order}. ` : '';
      const trimmedName = typeof action?.name === 'string' ? action.name.trim() : '';
      const displayName = `${orderPrefix}${trimmedName}`.trim();
      const normalizedName = trimmedName;
      const baseDisplay = displayName || normalizedName;
      const rawConsequenceIds = Array.isArray(action?.consequences) ? action.consequences : [];
      const consequenceIds = rawConsequenceIds
        .map((item: any) => {
          if (typeof item === 'number') return item;
          if (item && typeof item === 'object' && 'id' in item) {
            return (item as { id?: number }).id ?? null;
          }
          return null;
        })
        .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id));
      const optionKey =
        typeof action?.id === 'number' && !Number.isNaN(action.id)
          ? `action-${action.id}`
          : `action-${index}-${normalizeForSearch(normalizedName)}`;
      const optionValue = normalizedName || baseDisplay || optionKey;
      const searchParts = [baseDisplay, normalizedName].map((part) => normalizeForSearch(part)).filter(Boolean);
      const combinedSearchValue = Array.from(new Set(searchParts)).join(' ');
      return {
        key: optionKey,
        value: optionValue,
        label: <span>{baseDisplay}</span>,
        actionId: typeof action?.id === 'number' ? action.id : null,
        name: normalizedName,
        responsability: action?.responsability ?? '',
        asociated_risk: action?.asociated_risk ?? '',
        project_order:
          typeof action?.project_order === 'number' && !Number.isNaN(action.project_order)
            ? action.project_order
            : null,
        searchValue: combinedSearchValue,
        consequenceIds,
      };
    });
  }, [allProjectActions]);

  interface TableRow {
    key: React.Key;
    isNewEditable?: boolean;
    name?: string;
    causeId?: number;
    causeName?: string;
    causeOrder?: number;
    consequence?: ConsequenceType | null;
    consequenceOrder?: number;
    isFirstForCause?: boolean;
    isLastForCause?: boolean;
    isPlaceholder?: boolean;
    rowSpan?: number;
  }

  interface DeviationTableColumn {
    title: string | React.ReactNode;
    dataIndex?: string;
    key?: string;
    render?: (value: any, record?: TableRow) => React.ReactNode;
    children?: DeviationTableColumn[];
    onCell?: (record: TableRow) => any;
    width?: number;
  }

  const [severityLevelsByMatrix, setSeverityLevelsByMatrix] = useState<Record<number, any[]>>({});
  const [likelihoodLevelsByMatrix, setLikelihoodLevelsByMatrix] = useState<Record<number, any[]>>({});
  const [riskLevelsByMatrix, setRiskLevelsByMatrix] = useState<Record<number, MatrixRiskType[]>>({});

  const [loadingSeverityLevels, setLoadingSeverityLevels] = useState<Record<number, boolean>>({});
  const [loadingLikelihoodLevels, setLoadingLikelihoodLevels] = useState<Record<number, boolean>>({});
  const [loadingRiskLevels, setLoadingRiskLevels] = useState<Record<number, boolean>>({});
  const [matrixIdByConsequence, setMatrixIdByConsequence] = useState<Record<number, number | null>>({});
  const lastDeviationIdRef = useRef<number | null>(null);
  const [showMitigatedSeverity, setShowMitigatedSeverity] = useState(true);
  const [showProjectedSeverity, setShowProjectedSeverity] = useState(true);

  useEffect(() => {
    if (!projectId || !deviation?.id) {
      return;
    }
    void dispatch(getProjectActionsByProjectId(projectId) as any);
  }, [dispatch, projectId, deviation?.id]);

  type ConsequenceSelectionState = {
    matrixId: number | null;
    potentialSeverity: number | null;
    potentialLikelihood: number | null;
    potentialRisk: MatrixRiskType | null;
    mitigatedSeverity: number | null;
    mitigatedLikelihood: number | null;
    mitigatedRisk: MatrixRiskType | null;
    projectedSeverity: number | null;
    projectedLikelihood: number | null;
    projectedRisk: MatrixRiskType | null;
  };

  const createEmptySelectionState = (): ConsequenceSelectionState => ({
    matrixId: null,
    potentialSeverity: null,
    potentialLikelihood: null,
    potentialRisk: null,
    mitigatedSeverity: null,
    mitigatedLikelihood: null,
    mitigatedRisk: null,
    projectedSeverity: null,
    projectedLikelihood: null,
    projectedRisk: null,
  });

  const [consequenceSelections, setConsequenceSelections] = useState<Record<number, ConsequenceSelectionState>>({});

  useEffect(() => {
    if (!projectId || hasLoadedSafeguardNames || isLoadingSafeguardNames) {
      return;
    }
    void dispatch(getSafeguardNamesByProjectId(projectId) as any);
  }, [projectId, hasLoadedSafeguardNames, isLoadingSafeguardNames, dispatch]);

  useEffect(() => {
    if (!projectId || hasLoadedProjectActions || isLoadingProjectActions) {
      return;
    }
    void dispatch(getProjectActionsByProjectId(projectId) as any);
  }, [projectId, hasLoadedProjectActions, isLoadingProjectActions, dispatch]);

  useEffect(() => {
    if (!projectId || hasLoadedProjectCauses || isLoadingProjectCauses) {
      return;
    }
    void dispatch(getProjectCausesByProjectId(projectId) as any);
  }, [projectId, hasLoadedProjectCauses, isLoadingProjectCauses, dispatch]);

  useEffect(() => {
    if (!projectId || hasLoadedProjectConsequences || isLoadingProjectConsequences) {
      return;
    }
    void dispatch(getProjectConsequencesByProjectId(projectId) as any);
  }, [projectId, hasLoadedProjectConsequences, isLoadingProjectConsequences, dispatch]);

  const updateSelectionState = (consequenceId: number, updates: Partial<ConsequenceSelectionState>) => {
    setConsequenceSelections((prev) => {
      const previous = prev[consequenceId] ?? createEmptySelectionState();
      return {
        ...prev,
        [consequenceId]: {
          ...createEmptySelectionState(),
          ...previous,
          ...updates,
        },
      };
    });
  };

  useEffect(() => {
    const currentDeviationId = deviation?.id ?? null;
    if (currentDeviationId !== lastDeviationIdRef.current) {
      lastDeviationIdRef.current = currentDeviationId;
      setConsequenceSelections({});
      setMatrixIdByConsequence({});
    }
  }, [deviation?.id]);

  const getSelectionState = (consequenceId: number): ConsequenceSelectionState => {
    return consequenceSelections[consequenceId] ?? createEmptySelectionState();
  };

  const findRiskLevel = (
    matrixId: number | null,
    severityId: number | null,
    likelihoodId: number | null,
    riskLevelsOverride?: MatrixRiskType[]
  ): MatrixRiskType | null => {
    if (!matrixId || !severityId || !likelihoodId) {
      return null;
    }

    const riskLevels = (riskLevelsOverride ?? riskLevelsByMatrix[matrixId]) || [];
    const match = riskLevels.find((risk) => {
      const severityRelation = getRelationId(risk.severity ?? risk.severity_id);
      const likelihoodRelation = getRelationId(risk.likelihood ?? risk.likelihood_id);
      return severityRelation === severityId && likelihoodRelation === likelihoodId;
    });

    return match ?? null;
  };

  async function getRiskLevelsForMatrix(matrixId: number) {
    if (!matrixId) return;
    if (riskLevelsByMatrix[matrixId]) {
      return riskLevelsByMatrix[matrixId];
    }
    if (loadingRiskLevels[matrixId]) {
      return riskLevelsByMatrix[matrixId] || [];
    }

    setLoadingRiskLevels((prev) => ({ ...prev, [matrixId]: true }));

    try {
      const result = await dispatch(getRiskLevelsByMatrixId(matrixId) as any);
      if (getRiskLevelsByMatrixId.fulfilled.match(result)) {
        setRiskLevelsByMatrix((prev) => ({
          ...prev,
          [matrixId]: result.payload || [],
        }));
        return result.payload || [];
      } else {
        triggerNotification('error', t('Error'), t('No se pudieron cargar los niveles de riesgo'), 'bottomRight');
      }
    } catch (error: any) {
      triggerNotification('error', t('Error'), t('Error al cargar los niveles de riesgo'), 'bottomRight');
    } finally {
      setLoadingRiskLevels((prev) => ({ ...prev, [matrixId]: false }));
    }
  }

  async function getSeverityLevelsForRow(matrixId: number, consequenceId?: number) {
    if (!matrixId) return [];

    if (consequenceId) {
      setMatrixIdByConsequence((prev) => {
        const previousValue = prev[consequenceId];
        if (previousValue === matrixId) {
          return prev;
        }
        return { ...prev, [consequenceId]: matrixId };
      });
    }

    if (severityLevelsByMatrix[matrixId]) {
      return severityLevelsByMatrix[matrixId];
    }

    if (loadingSeverityLevels[matrixId]) {
      return severityLevelsByMatrix[matrixId] || [];
    }

    setLoadingSeverityLevels((prev) => ({ ...prev, [matrixId]: true }));

    try {
      const result = await dispatch(getSeverityLevelsByMatrixId(matrixId) as any);
      if (getSeverityLevelsByMatrixId.fulfilled.match(result)) {
        setSeverityLevelsByMatrix((prev) => ({
          ...prev,
          [matrixId]: result.payload || [],
        }));
        return result.payload || [];
      } else {
        triggerNotification('error', t('Error'), t('No se pudieron cargar los niveles de severidad'), 'bottomRight');
      }
    } catch (error: any) {
      triggerNotification('error', t('Error'), t('Error al cargar los niveles de severidad'), 'bottomRight');
    } finally {
      setLoadingSeverityLevels((prev) => ({ ...prev, [matrixId]: false }));
    }

    return [];
  }

  async function getLikelihoodLevelsForRow(matrixId: number, consequenceId?: number) {
    if (!matrixId) return [];

    if (consequenceId) {
      setMatrixIdByConsequence((prev) => {
        const previousValue = prev[consequenceId];
        if (previousValue === matrixId) {
          return prev;
        }
        return { ...prev, [consequenceId]: matrixId };
      });
    }

    if (likelihoodLevelsByMatrix[matrixId]) {
      return likelihoodLevelsByMatrix[matrixId];
    }

    if (loadingLikelihoodLevels[matrixId]) {
      return likelihoodLevelsByMatrix[matrixId] || [];
    }

    setLoadingLikelihoodLevels((prev) => ({ ...prev, [matrixId]: true }));

    try {
      const result = await dispatch(getLikelihoodLevelsByMatrixId(matrixId) as any);
      if (getLikelihoodLevelsByMatrixId.fulfilled.match(result)) {
        setLikelihoodLevelsByMatrix((prev) => ({
          ...prev,
          [matrixId]: result.payload || [],
        }));
        return result.payload || [];
      } else {
        triggerNotification('error', t('Error'), t('No se pudieron cargar los niveles de probabilidad'), 'bottomRight');
      }
    } catch (error: any) {
      triggerNotification('error', t('Error'), t('Error al cargar los niveles de probabilidad'), 'bottomRight');
    } finally {
      setLoadingLikelihoodLevels((prev) => ({ ...prev, [matrixId]: false }));
    }

    return [];
  }

  const ensureMatrixDataLoaded = async (matrixId: number, consequenceId?: number) => {
    if (!matrixId) return;
    await Promise.all([
      getSeverityLevelsForRow(matrixId, consequenceId),
      getLikelihoodLevelsForRow(matrixId, consequenceId),
      getRiskLevelsForMatrix(matrixId),
    ]);
  };

  type RiskColumnKey = 'potential' | 'mitigated' | 'projected';

  const resolveMatrixIdForConsequence = (consequence?: ConsequenceType | null): number | null => {
    const consequenceId = consequence?.id;
    if (!consequence || consequenceId === null || consequenceId === undefined) {
      return extractMatrixId(consequence);
    }

    const selection = consequenceSelections[consequenceId];
    if (selection && selection.matrixId !== null) {
      return selection.matrixId;
    }

    if (matrixIdByConsequence[consequenceId] !== undefined) {
      return matrixIdByConsequence[consequenceId] ?? null;
    }

    return extractMatrixId(consequence);
  };

  const toggleMitigatedSeverityVisibility = () => setShowMitigatedSeverity((prev) => !prev);
  const toggleProjectedSeverityVisibility = () => setShowProjectedSeverity((prev) => !prev);

  const getMatrixId = (value: MatrixType | number | null | undefined): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && 'id' in value) {
      return (value as MatrixType).id ?? null;
    }
    return null;
  };

  const extractMatrixId = (consequence?: ConsequenceType | null): number | null => {
    if (!consequence) return null;

    const matrixIds = [
      getMatrixId(consequence.potential_risk_matrix),
      getMatrixId(consequence.mitigated_risk_matrix),
      getMatrixId(consequence.projected_risk_matrix),
      getMatrixId((consequence as any).risk_matrix),
    ];

    for (const id of matrixIds) {
      if (id !== null && id !== undefined) {
        return id;
      }
    }

    return null;
  };

  const getRelationId = (value: any): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && value !== null && 'id' in value) {
      return (value as { id?: number }).id ?? null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const resolveSelectOptionLabel = (item: any, fallback: string): string => {
    const labelSource =
      typeof item === 'string'
        ? item
        : typeof item?.name === 'string'
        ? item.name
        : typeof item?.label === 'string'
        ? item.label
        : '';
    const trimmed = typeof labelSource === 'string' ? labelSource.trim() : '';
    return trimmed.length ? trimmed : fallback;
  };

  const toNumericSelectOptions = (items: any[] = []): { value: number; label: string }[] =>
    items
      .map((item) => {
        const candidate = item?.id ?? item;
        const value = getRelationId(candidate);
        if (value === null) {
          return null;
        }
        const label = resolveSelectOptionLabel(item, `${value}`);
        return {
          value,
          label,
        };
      })
      .filter((option): option is { value: number; label: string } => option !== null);

  const toSelectOptionsWithDisplayLabel = (items: any[] = []): { value: number; label: string; displayLabel: string }[] =>
    items
      .map((item) => {
        const candidate = item?.id ?? item;
        const value = getRelationId(candidate);
        if (value === null) {
          return null;
        }
        const label = resolveSelectOptionLabel(item, `${value}`);
        return {
          value,
          label,
          displayLabel: label,
        };
      })
      .filter((option): option is { value: number; label: string; displayLabel: string } => option !== null);

  const getLevelDescription = (level: any): string | null => {
    const description = typeof level?.description === 'string' ? level.description.trim() : '';
    return description.length ? description : null;
  };
  const getLevelById = (items: any[] = [], value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return null;
    }
    return items.find((item) => getRelationId(item?.id ?? item) === value) ?? null;
  };
  const renderLevelSelectWithTooltip = ({
    value,
    items,
    placeholder,
    onChange,
  }: {
    value: number | null;
    items: any[];
    placeholder: string;
    onChange: (value: number) => void | Promise<void>;
  }) => {
    const selectedLevel = getLevelById(items, value);
    const tooltipTitle = getLevelDescription(selectedLevel);

    return (
      <Tooltip title={tooltipTitle} trigger={['hover', 'focus']} destroyTooltipOnHide mouseEnterDelay={0.05}>
        <Select<number>
          value={value ?? undefined}
          onChange={onChange}
          className='matrix-select'
          popupClassName='matrix-select-dropdown'
          dropdownMatchSelectWidth={false}
          placeholder={placeholder}
          size='small'
          optionLabelProp='displayLabel'
          options={toSelectOptionsWithDisplayLabel(items)}
        />
      </Tooltip>
    );
  };
  const getRiskName = (risk: MatrixRiskType | number | null | undefined): string | null => {
    if (!risk && risk !== 0) return null;
    if (typeof risk === 'number') return risk.toString();
    return risk?.name ?? null;
  };

  const getRiskDescription = (risk: MatrixRiskType | number | null | undefined): string | null => {
    if (!risk || typeof risk === 'number') {
      return null;
    }
    const description = risk.description?.trim();
    return description && description.length ? description : null;
  };

  const getRiskAbbreviation = (label: string | null | undefined, maxLength = 3): string | null => {
    if (!label) return null;
    const compactValue = label.replace(/\s+/g, '').trim();
    if (!compactValue.length) {
      return null;
    }
    const shortened = compactValue.slice(0, maxLength).toUpperCase();
    return shortened || null;
  };

  const normalizeColorCode = (color: string | null | undefined): string | null => {
    if (!color) {
      return null;
    }
    const trimmed = color.trim();
    if (!trimmed.length) {
      return null;
    }
    const normalized = trimmed.replace(/^#/, '');
    return normalized ? `#${normalized}` : null;
  };

  const isEmptyRiskLabel = (value: string | null | undefined): boolean => {
    if (value === null || value === undefined) return true;
    const normalized = value.trim();
    return normalized === '' || normalized === '-';
  };

  const defaultRiskColor = '#f2f2f2';

  type RiskDisplayData = {
    label: string | null;
    description: string | null;
    backgroundColor: string;
    fontColor: string | null;
  };

  const getRiskDisplayData = (
    consequence: ConsequenceType | null | undefined,
    riskKey: RiskColumnKey
  ): RiskDisplayData => {
    const consequenceId = consequence?.id;
    if (!consequence || consequenceId === null || consequenceId === undefined) {
      return { label: null, description: null, backgroundColor: defaultRiskColor, fontColor: null };
    }

    if (consequenceId === null || consequenceId === undefined) {
      return { label: null, description: null, backgroundColor: defaultRiskColor, fontColor: null };
    }

    const selection = getSelectionState(consequenceId);

    let selectionRisk: MatrixRiskType | null = null;
    let consequenceRisk: MatrixRiskType | number | null | undefined;
    let consequenceColor: string | null | undefined;

    switch (riskKey) {
      case 'potential':
        selectionRisk = selection.potentialRisk;
        consequenceRisk = consequence.potential_risk_matrix_risk;
        consequenceColor = consequence.potential_risk_matrix_risk_color;
        break;
      case 'mitigated':
        selectionRisk = selection.mitigatedRisk;
        consequenceRisk = consequence.mitigated_risk_matrix_risk;
        consequenceColor = consequence.mitigated_risk_matrix_risk_color;
        break;
      case 'projected':
        selectionRisk = selection.projectedRisk;
        consequenceRisk = consequence.projected_risk_matrix_risk;
        consequenceColor = consequence.projected_risk_matrix_risk_color;
        break;
      default:
        selectionRisk = null;
        consequenceRisk = null;
        consequenceColor = null;
    }

    const riskSource = selectionRisk ?? consequenceRisk;
    const label = getRiskName(riskSource);
    const emptyRiskValue = isEmptyRiskLabel(label ?? null);

    const selectionColor =
      selectionRisk && typeof selectionRisk === 'object' && 'cell_color' in selectionRisk
        ? (selectionRisk as MatrixRiskType & { cell_color?: string }).cell_color
        : undefined;

    const riskObjectColor =
      consequenceRisk && typeof consequenceRisk === 'object' && 'cell_color' in consequenceRisk
        ? (consequenceRisk as MatrixRiskType & { cell_color?: string }).cell_color
        : undefined;

    const rawColor = selectionColor ?? riskObjectColor ?? consequenceColor ?? null;
    const backgroundColor = emptyRiskValue ? '#ffffff' : normalizeColorCode(rawColor) ?? defaultRiskColor;
    const fontColorSource =
      (selectionRisk && typeof selectionRisk === 'object' ? selectionRisk.font_color : null) ??
      (consequenceRisk && typeof consequenceRisk === 'object'
        ? (consequenceRisk as MatrixRiskType).font_color
        : null) ??
      null;
    const fontColor = emptyRiskValue ? null : normalizeColorCode(fontColorSource);

    return {
      label: emptyRiskValue ? null : label,
      description: getRiskDescription(riskSource),
      backgroundColor,
      fontColor,
    };
  };

  const renderRiskBadge = ({ label, description, fontColor }: RiskDisplayData) => {
    const shortLabel = getRiskAbbreviation(label);
    const tooltipTitle = description || '-';
    return (
      <Tooltip title={tooltipTitle} trigger={['hover', 'click']} destroyTooltipOnHide mouseEnterDelay={0.05}>
        <div
          tabIndex={0}
          aria-label={`Valor de riesgo: ${label || 'sin dato'}`}
          className='risk-color-block'
          style={{ color: fontColor ?? undefined }}
        >
          {shortLabel ?? '-'}
        </div>
      </Tooltip>
    );
  };

  const mergeItemsById = <T extends { id?: number | null }>(
    primary?: (T | null)[] | null,
    secondary?: (T | null)[] | null
  ): T[] => {
    const result: T[] = [];
    const indexById = new Map<number, number>();

    const addItems = (items?: (T | null)[] | null) => {
      (items ?? []).forEach((item) => {
        if (!item) {
          return;
        }

        const { id } = item;
        if (typeof id === 'number') {
          const existingIndex = indexById.get(id);
          if (existingIndex !== undefined) {
            const merged = { ...result[existingIndex], ...item };
            result[existingIndex] = merged;
          } else {
            indexById.set(id, result.length);
            result.push({ ...item });
          }
        } else {
          result.push({ ...item });
        }
      });
    };

    addItems(primary);
    addItems(secondary);

    return result;
  };

  const consequencesByCauseId = useMemo(() => {
    const grouped: Record<number, ConsequenceType[]> = {};

    (deviation.consequences ?? []).forEach((consequence) => {
      const causeIds =
        consequence.causes?.map((cause) => cause.id).filter((id): id is number => typeof id === 'number') ?? [];

      causeIds.forEach((causeId) => {
        if (!grouped[causeId]) {
          grouped[causeId] = [];
        }
        grouped[causeId].push(consequence);
      });
    });

    return grouped;
  }, [deviation.consequences]);

  const sortedCauses = useMemo(() => {
    const causes = (deviation.causes ?? []).filter(
      (cause): cause is CauseType => !!cause && cause.id !== null && cause.id !== undefined
    );

    return [...causes].sort((a, b) => {
      const explicitOrder = compareByOrderThenId(a, b, 'order');
      const aHasOrder = typeof a.order === 'number' && !Number.isNaN(a.order);
      const bHasOrder = typeof b.order === 'number' && !Number.isNaN(b.order);
      if ((aHasOrder || bHasOrder) && explicitOrder !== 0) {
        return explicitOrder;
      }

      const createdA = a.created_at ? Date.parse(a.created_at) : null;
      const createdB = b.created_at ? Date.parse(b.created_at) : null;
      const hasCreatedA = typeof createdA === 'number' && !Number.isNaN(createdA);
      const hasCreatedB = typeof createdB === 'number' && !Number.isNaN(createdB);

      if (hasCreatedA && hasCreatedB && createdA !== createdB) {
        return createdA - createdB;
      }

      if (hasCreatedA && !hasCreatedB) {
        return -1;
      }

      if (!hasCreatedA && hasCreatedB) {
        return 1;
      }

      const idA = typeof a.id === 'number' ? a.id : Number.MAX_SAFE_INTEGER;
      const idB = typeof b.id === 'number' ? b.id : Number.MAX_SAFE_INTEGER;

      if (idA !== idB) {
        return idA - idB;
      }

      return 0;
    });
  }, [deviation.causes]);

  useEffect(() => {
    const loadInitialLevels = async () => {
      const consequences = deviation.consequences ?? [];
      if (!consequences.length) {
        return;
      }

      await Promise.all(
        consequences.map(async (consequence) => {
          const consequenceId = consequence?.id;
          if (!consequenceId) {
            return;
          }

          const matrixId = extractMatrixId(consequence);

          setMatrixIdByConsequence((prev) => {
            const previous = prev[consequenceId];
            if (previous === matrixId) {
              return prev;
            }
            return {
              ...prev,
              [consequenceId]: matrixId ?? null,
            };
          });

          let loadedRiskLevels: MatrixRiskType[] | undefined;

          if (matrixId) {
            const [, , riskLevelsResult] = await Promise.all([
              getSeverityLevelsForRow(matrixId, consequenceId),
              getLikelihoodLevelsForRow(matrixId, consequenceId),
              getRiskLevelsForMatrix(matrixId),
            ]);
            if (Array.isArray(riskLevelsResult)) {
              loadedRiskLevels = riskLevelsResult;
            }
          }

          setConsequenceSelections((prev) => {
            const previousSelection = prev[consequenceId] ?? createEmptySelectionState();
            const nextSelection: ConsequenceSelectionState = {
              ...createEmptySelectionState(),
              ...previousSelection,
            };

            if (nextSelection.matrixId === null && matrixId !== null) {
              nextSelection.matrixId = matrixId;
            }

            const potentialSeverity = getRelationId(consequence.potential_risk_matrix_severity);
            if (nextSelection.potentialSeverity === null && potentialSeverity !== null) {
              nextSelection.potentialSeverity = potentialSeverity;
            }

            const potentialLikelihood = getRelationId(consequence.potential_risk_matrix_likelihood);
            if (nextSelection.potentialLikelihood === null && potentialLikelihood !== null) {
              nextSelection.potentialLikelihood = potentialLikelihood;
            }

            const mitigatedSeverity = getRelationId(consequence.mitigated_risk_matrix_severity);
            if (nextSelection.mitigatedSeverity === null && mitigatedSeverity !== null) {
              nextSelection.mitigatedSeverity = mitigatedSeverity;
            }

            const mitigatedLikelihood = getRelationId(consequence.mitigated_risk_matrix_likelihood);
            if (nextSelection.mitigatedLikelihood === null && mitigatedLikelihood !== null) {
              nextSelection.mitigatedLikelihood = mitigatedLikelihood;
            }

            const projectedSeverity = getRelationId(consequence.projected_risk_matrix_severity);
            if (nextSelection.projectedSeverity === null && projectedSeverity !== null) {
              nextSelection.projectedSeverity = projectedSeverity;
            }

            const projectedLikelihood = getRelationId(consequence.projected_risk_matrix_likelihood);
            if (nextSelection.projectedLikelihood === null && projectedLikelihood !== null) {
              nextSelection.projectedLikelihood = projectedLikelihood;
            }

            const effectiveMatrixId = nextSelection.matrixId;
            const riskLevels =
              loadedRiskLevels ?? (effectiveMatrixId ? riskLevelsByMatrix[effectiveMatrixId] || [] : []);

            const resolveRiskFromConsequence = (
              riskValue: MatrixRiskType | MatrixRiskType['id'] | null | undefined
            ): MatrixRiskType | null => {
              if (!riskValue && riskValue !== 0) {
                return null;
              }

              if (typeof riskValue === 'object') {
                return riskValue as MatrixRiskType;
              }

              const parsedId = getRelationId(riskValue);
              if (parsedId !== null && riskLevels.length) {
                return riskLevels.find((risk) => getRelationId(risk.id) === parsedId) ?? null;
              }

              return null;
            };

            if (
              nextSelection.potentialRisk === null &&
              effectiveMatrixId &&
              nextSelection.potentialSeverity !== null &&
              nextSelection.potentialLikelihood !== null
            ) {
              nextSelection.potentialRisk = findRiskLevel(
                effectiveMatrixId,
                nextSelection.potentialSeverity,
                nextSelection.potentialLikelihood,
                riskLevels
              );
            }

            if (nextSelection.potentialRisk === null) {
              nextSelection.potentialRisk = resolveRiskFromConsequence(consequence.potential_risk_matrix_risk);
            }

            if (
              nextSelection.mitigatedRisk === null &&
              effectiveMatrixId &&
              nextSelection.mitigatedSeverity !== null &&
              nextSelection.mitigatedLikelihood !== null
            ) {
              nextSelection.mitigatedRisk = findRiskLevel(
                effectiveMatrixId,
                nextSelection.mitigatedSeverity,
                nextSelection.mitigatedLikelihood,
                riskLevels
              );
            }

            if (nextSelection.mitigatedRisk === null) {
              nextSelection.mitigatedRisk = resolveRiskFromConsequence(consequence.mitigated_risk_matrix_risk);
            }

            if (
              nextSelection.projectedRisk === null &&
              effectiveMatrixId &&
              nextSelection.projectedSeverity !== null &&
              nextSelection.projectedLikelihood !== null
            ) {
              nextSelection.projectedRisk = findRiskLevel(
                effectiveMatrixId,
                nextSelection.projectedSeverity,
                nextSelection.projectedLikelihood,
                riskLevels
              );
            }

            if (nextSelection.projectedRisk === null) {
              nextSelection.projectedRisk = resolveRiskFromConsequence(consequence.projected_risk_matrix_risk);
            }

            return {
              ...prev,
              [consequenceId]: nextSelection,
            };
          });
        })
      );
    };

    void loadInitialLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviation, dispatch, triggerNotification]);

  // Estado para controlar múltiples inputs de nuevas consecuencias por causa
  const [newConsequenceInputs, setNewConsequenceInputs] = useState<Record<number, string>>({});

  // Añadir un input para una causa específica
  const handleAddConsequenceInput = (causeId: number) => {
    setNewConsequenceInputs((prev) => ({
      ...prev,
      [causeId]: '', // Inicializamos con cadena vacía
    }));
  };

  // Manejar cambio de valor en el input
  const handleConsequenceInputChange = (causeId: number, value: string) => {
    setNewConsequenceInputs((prev) => ({
      ...prev,
      [causeId]: value,
    }));
  };

  // Guardar una consecuencia
  const handleNewConsequenceSave = (causeId: number) => {
    const value = newConsequenceInputs[causeId]?.trim();
    if (value) {
      // Crear la nueva consecuencia asociada a la causa
      const newConsequence: CreateConsequenceType = {
        deviation: deviation.id ?? 0,
        name: value,
        causes: [{ id: causeId }], // Asociar a la causa correspondiente
      };
      dispatch(createConsequence(newConsequence) as any)
        .then((result: any) => {
          if (createConsequence.fulfilled.match(result)) {
            triggerNotification(
              'success',
              t('Consecuencia creada'),
              t('La consecuencia ha sido creada correctamente'),
              'bottomRight'
            );
            // Limpiar solo este input
            setNewConsequenceInputs((prev) => {
              const updated = { ...prev };
              delete updated[causeId];
              return updated;
            });
            if (deviation.id !== null && deviation.id !== undefined) {
              void dispatch(getDeviation(deviation.id) as any);
            }
          }
        })
        .catch((error: any) => {
          triggerNotification('error', t('Error'), error.error, 'bottomRight');
        });
    }
  };

  // Estado para manejar múltiples filas nuevas de causa
  const [newCauses, setNewCauses] = useState<{ key: number; name: string }[]>([]);

  // Generador de claves únicas para nuevas filas
  const [newKey, setNewKey] = useState(-1);

  // Añadir una nueva fila editable de causa
  const handleAddNewCauseRow = () => {
    setNewCauses((prev) => [...prev, { key: newKey, name: '' }]);
    setNewKey((prev) => prev - 1);
  };

  // Manejar el cambio en el input de la nueva causa
  const handleChangeCauseInput = (value: string, key: number) => {
    setNewCauses((prev) => prev.map((cause) => (cause.key === key ? { ...cause, name: value } : cause)));
  };

  // Guardar la nueva causa usando el valor correcto de la fila
  const handleNewCauseSave = (key: number) => {
    const causeObj = newCauses.find((c) => c.key === key);
    if (!causeObj || causeObj.name.trim() === '') {
      triggerNotification('error', t('Error'), t('El nombre de la causa no puede estar vacío'), 'bottomRight');
      return;
    }
    const newCause: CreateCauseType = {
      deviation: deviation.id ?? 0,
      name: causeObj.name.trim(),
    };
    dispatch(createCause(newCause) as any)
      .then((result: any) => {
        if (createCause.fulfilled.match(result)) {
          triggerNotification('success', t('Causa creada'), t('La causa ha sido creada correctamente'), 'bottomRight');
          // Eliminar la fila editable después de guardar
          setNewCauses((prev) => prev.filter((c) => c.key !== key));
          if (deviation.id !== null && deviation.id !== undefined) {
            void dispatch(getDeviation(deviation.id) as any);
          }
        }
      })
      .catch((error: any) => {
        triggerNotification('error', t('Error'), error.error, 'bottomRight');
      });
  };

  const [showExistingCauseSelector, setShowExistingCauseSelector] = useState(false);
  const [selectedExistingCauseId, setSelectedExistingCauseId] = useState<number | null>(null);
  const [existingConsequenceSelectorCauseId, setExistingConsequenceSelectorCauseId] = useState<number | null>(null);
  const [selectedExistingConsequenceIdByCause, setSelectedExistingConsequenceIdByCause] = useState<Record<number, number | null>>(
    {}
  );

  const currentDeviationId = typeof deviation?.id === 'number' ? deviation.id : null;
  const currentCauseIdSet = useMemo(
    () =>
      new Set<number>(
        (deviation?.causes ?? [])
          .map((cause) => cause?.id)
          .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id))
      ),
    [deviation?.causes]
  );

  const existingCauseOptions = useMemo(
    () =>
      projectCausesForProject
        .filter((cause) => typeof cause.id === 'number' && !currentCauseIdSet.has(cause.id))
        .map((cause) => ({
          value: cause.id as number,
          label: `${cause.reference_code ? `${cause.reference_code} ` : ''}${cause.name}`,
        })),
    [projectCausesForProject, currentCauseIdSet]
  );

  const getExistingConsequenceOptions = (causeId: number) => {
    const linkedConsequenceIds = new Set<number>(
      ((deviation?.causes ?? []).find((item) => item?.id === causeId)?.consequences ?? [])
        .map((item) => item?.id)
        .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id))
    );
    return projectConsequencesForProject
      .filter((consequence) => typeof consequence.id === 'number' && !linkedConsequenceIds.has(consequence.id))
      .map((consequence) => ({
        value: consequence.id as number,
        label: `${consequence.reference_code ? `${consequence.reference_code} ` : ''}${consequence.name}`,
      }));
  };

  const navigateToReference = (
    referenceDeviationId?: number | null,
    elementId?: string | null
  ) => {
    if (!referenceDeviationId) {
      return;
    }
    if (currentDeviationId === referenceDeviationId && elementId) {
      document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    onNavigateToDeviation?.(referenceDeviationId);
  };

  const handleLinkExistingCause = () => {
    if (!selectedExistingCauseId || !currentDeviationId) {
      return;
    }
    dispatch(assignCauseToDeviation({ causeId: selectedExistingCauseId, deviationId: currentDeviationId }) as any)
      .then((result: any) => {
        if (assignCauseToDeviation.fulfilled.match(result)) {
          triggerNotification('success', t('Causa enlazada'), t('La causa se enlazó sin duplicar'), 'bottomRight');
          setSelectedExistingCauseId(null);
          setShowExistingCauseSelector(false);
          void dispatch(getDeviation(currentDeviationId) as any);
          if (projectId) {
            void dispatch(getProjectCausesByProjectId(projectId) as any);
          }
        } else {
          const errorMessage = extractErrorMessage(result?.payload) ?? t('No se pudo enlazar la causa');
          triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
        }
      })
      .catch((error: any) => {
        const errorMessage = extractErrorMessage(error?.response?.data ?? error) ?? t('No se pudo enlazar la causa');
        triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
      });
  };

  const handleLinkExistingConsequence = (causeId: number) => {
    const consequenceId = selectedExistingConsequenceIdByCause[causeId];
    if (!consequenceId) {
      return;
    }
    dispatch(assignConsequenceToCause({ consequenceId, causeId }) as any)
      .then((result: any) => {
        if (assignConsequenceToCause.fulfilled.match(result)) {
          triggerNotification(
            'success',
            t('Consecuencia enlazada'),
            t('La consecuencia se enlazó sin duplicar'),
            'bottomRight'
          );
          setExistingConsequenceSelectorCauseId(null);
          setSelectedExistingConsequenceIdByCause((prev) => ({ ...prev, [causeId]: null }));
          if (currentDeviationId) {
            void dispatch(getDeviation(currentDeviationId) as any);
          }
          if (projectId) {
            void dispatch(getProjectConsequencesByProjectId(projectId) as any);
          }
        } else {
          const errorMessage = extractErrorMessage(result?.payload) ?? t('No se pudo enlazar la consecuencia');
          triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
        }
      })
      .catch((error: any) => {
        const errorMessage =
          extractErrorMessage(error?.response?.data ?? error) ?? t('No se pudo enlazar la consecuencia');
        triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
      });
  };

  // Estado para controlar múltiples inputs de nuevas salvaguardas por consecuencia
  const [newSafeguardInputs, setNewSafeguardInputs] = useState<Record<number, string>>({});

  // Añadir un input para una consecuencia específica
  const handleAddSafeguardInput = (consequenceId: number) => {
    if (projectId && !safeguardNamesForProject.length && !isLoadingSafeguardNames) {
      void dispatch(getSafeguardNamesByProjectId(projectId) as any);
    }
    setNewSafeguardInputs((prev) => ({
      ...prev,
      [consequenceId]: '',
    }));
  };

  // Manejar cambio de valor en el input
  const handleSafeguardInputChange = (consequenceId: number, value: string) => {
    setNewSafeguardInputs((prev) => ({
      ...prev,
      [consequenceId]: value,
    }));
  };

  const handleSafeguardAutocompleteSelect = (consequenceId: number, value: string) => {
    handleSafeguardInputChange(consequenceId, value);
  };

  // Guardar una salvaguarda
  const handleNewSafeguardSave = (consequenceId: number) => {
    const value = newSafeguardInputs[consequenceId]?.trim();
    if (value) {
      const consequenceFromDeviation = (deviation.consequences ?? []).find((item) => item?.id === consequenceId);
      const consequenceFromCause = (deviation.causes ?? [])
        .flatMap((cause) => cause.consequences ?? [])
        .find((item) => item?.id === consequenceId);
      const currentSafeguards = [
        ...(consequenceFromDeviation?.safeguards ?? []),
        ...(consequenceFromCause?.safeguards ?? []),
      ];
      const alreadyExists = currentSafeguards.some(
        (safeguard) => normalizeForSearch(safeguard?.name) === normalizeForSearch(value)
      );
      if (alreadyExists) {
        triggerNotification(
          'warning',
          t('Salvaguarda existente'),
          t('Esta consecuencia ya tiene asociada esa salvaguarda'),
          'bottomRight'
        );
        setNewSafeguardInputs((prev) => {
          const updated = { ...prev };
          delete updated[consequenceId];
          return updated;
        });
        return;
      }
      const newSafeguard: CreateSafeguardType = {
        deviation: deviation.id ?? 0,
        name: value,
        consequences: [{ id: consequenceId }],
      };
      dispatch(createSafeguard(newSafeguard) as any)
        .then((result: any) => {
          if (createSafeguard.fulfilled.match(result)) {
            triggerNotification(
              'success',
              t('Salvaguarda creada'),
              t('La salvaguarda ha sido creada correctamente'),
              'bottomRight'
            );
            setNewSafeguardInputs((prev) => {
              const updated = { ...prev };
              delete updated[consequenceId];
              return updated;
            });
            if (deviation.id !== null && deviation.id !== undefined) {
              void dispatch(getDeviation(deviation.id) as any);
            }
          }
        })
        .catch((error: any) => {
          triggerNotification('error', t('Error'), error.error, 'bottomRight');
        });
    }
  };

  // Estado para controlar múltiples inputs de nuevas acciones por consecuencia
  type NewActionInputState = {
    name: string;
    responsability?: string;
    asociated_risk?: string;
    actionId?: number | null;
    project_order?: number | null;
    isExisting?: boolean;
    existingConsequenceIds?: number[];
    skipResetOnce?: boolean;
  };
  const [newActionInputs, setNewActionInputs] = useState<Record<number, NewActionInputState>>({});
  type ActionDeleteRecord = ActionType & {
    consequenceId?: number | null;
    consequence_id?: number | null;
    actionId?: number | null;
  };

  const ensureProjectActionsLoaded = () => {
    if (projectId && !hasLoadedProjectActions && !isLoadingProjectActions) {
      void dispatch(getProjectActionsByProjectId(projectId) as any);
    }
  };

  // Añadir un input para una consecuencia específica
  const handleAddActionInput = (consequenceId: number) => {
    ensureProjectActionsLoaded();
    setNewActionInputs((prev) => ({
      ...prev,
      [consequenceId]: {
        name: '',
        responsability: '',
        asociated_risk: '',
        actionId: null,
        project_order: null,
        existingConsequenceIds: [],
        skipResetOnce: false,
      },
    }));
  };

  // Manejar cambio de valor en el input
  const handleActionInputChange = (consequenceId: number, field: keyof NewActionInputState, value: string) => {
    setNewActionInputs((prev) => {
      const current = prev[consequenceId] ?? {
        name: '',
        responsability: '',
        asociated_risk: '',
        actionId: null,
        project_order: null,
        isExisting: false,
        existingConsequenceIds: [],
        skipResetOnce: false,
      };
      const hadExisting = !!current.actionId;
      const next: NewActionInputState = {
        ...current,
        [field]: value,
        skipResetOnce: false,
      };
      const shouldPreserveExisting = current.skipResetOnce;
      if (hadExisting && !shouldPreserveExisting) {
        next.actionId = null;
        next.project_order = null;
        next.isExisting = false;
        next.existingConsequenceIds = [];
      }
      if (field === 'name' && !next.name) {
        next.isExisting = false;
      }
      return {
        ...prev,
        [consequenceId]: next,
      };
    });
  };

  const handleActionAutocompleteSelect = (consequence: ConsequenceType, option: ActionAutoCompleteOption) => {
    const consequenceId = consequence?.id;
    if (consequenceId === null || consequenceId === undefined) {
      return;
    }
    const rawId = option?.actionId;
    const parsedId = typeof rawId === 'number' && !Number.isNaN(rawId) ? rawId : null;
    if (!parsedId) {
      return;
    }
    const existingActions = consequence?.actions ?? [];
    const alreadyLinked = existingActions.some((item) => item.id === parsedId);
    if (alreadyLinked) {
      triggerNotification(
        'warning',
        t('Acción duplicada'),
        t('Esta consecuencia ya tiene asociada la recomendación seleccionada'),
        'bottomRight'
      );
      return;
    }
    setNewActionInputs((prev) => ({
      ...prev,
      [consequenceId]: {
        name: option?.name ?? '',
        responsability: option?.responsability ?? '',
        asociated_risk: option?.asociated_risk ?? '',
        actionId: parsedId,
        project_order: option?.project_order ?? null,
        isExisting: true,
        existingConsequenceIds: (option?.consequenceIds ?? []).filter((id) => currentDeviationConsequenceIdSet.has(id)),
        skipResetOnce: true,
      },
    }));
  };

  const getNextProjectActionOrder = (): number => {
    const combinedActions = [
      ...projectActionsForProject,
      ...(Array.isArray(deviation?.actions) ? deviation.actions : []),
    ];
    const orders = combinedActions
      .map((action) => action?.project_order)
      .filter((order): order is number => typeof order === 'number' && !Number.isNaN(order));
    if (!orders.length) {
      return 1;
    }
    return Math.max(...orders) + 1;
  };

  // Guardar una acción
  const handleNewActionSave = (consequenceId: number) => {
    const current = newActionInputs[consequenceId];
    const trimmedName = current?.name?.trim() ?? '';
    const actionId = current?.actionId ?? null;
    if (!trimmedName && !actionId) {
      return;
    }
    if (actionId) {
      if (!projectId) {
        triggerNotification(
          'error',
          t('Error'),
          t('No se pudo identificar el proyecto para asociar la recomendación'),
          'bottomRight'
        );
        return;
      }
      const existingIds = current?.existingConsequenceIds ?? [];
      const validExistingIds = existingIds.filter((id) => currentDeviationConsequenceIdSet.has(id));
      const payloadConsequenceIds = Array.from(new Set([...validExistingIds, consequenceId])).filter(
        (id): id is number => typeof id === 'number' && !Number.isNaN(id)
      );

      dispatch(
        assignActionToConsequence({
          actionId,
          consequenceId,
          deviationId: deviation.id ?? null,
          consequenceIds: payloadConsequenceIds,
        }) as any
      )
        .then((result: any) => {
          if (assignActionToConsequence.fulfilled.match(result)) {
            triggerNotification(
              'success',
              t('Recomendación asociada'),
              t('La recomendación se ha reutilizado correctamente'),
              'bottomRight'
            );
            setNewActionInputs((prev) => {
              const updated = { ...prev };
              delete updated[consequenceId];
              return updated;
            });
            if (deviation.id !== null && deviation.id !== undefined) {
              void dispatch(getDeviation(deviation.id) as any);
            }
          } else {
            const errorMessage = extractErrorMessage(result?.payload) ?? t('No se pudo asociar la recomendación');
            triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
          }
        })
        .catch((error: any) => {
          const errorMessage =
            extractErrorMessage(error?.response?.data ?? error?.error ?? error) ??
            t('No se pudo asociar la recomendación');
          triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
        });
      return;
    }
    if (!trimmedName) {
      triggerNotification('error', t('Error'), t('El nombre de la acción no puede estar vacío'), 'bottomRight');
      return;
    }

    const matchingExistingAction = allProjectActions.find((item) => {
      const itemId = normalizeActionId(item?.id);
      return itemId !== null && normalizeForSearch(item?.name) === normalizeForSearch(trimmedName);
    });
    const matchingActionId = normalizeActionId(matchingExistingAction?.id);
    if (matchingExistingAction && matchingActionId !== null) {
      const targetConsequence = (deviation.consequences ?? [])
        .concat((deviation.causes ?? []).flatMap((cause) => cause.consequences ?? []))
        .find((item) => item?.id === consequenceId);
      const alreadyLinked = (targetConsequence?.actions ?? []).some((item) => item?.id === matchingActionId);
      if (alreadyLinked) {
        triggerNotification(
          'warning',
          t('Recomendación existente'),
          t('Esta consecuencia ya tiene asociada esa recomendación'),
          'bottomRight'
        );
        setNewActionInputs((prev) => {
          const updated = { ...prev };
          delete updated[consequenceId];
          return updated;
        });
        return;
      }

      const existingIds = toNumericConsequenceIds(matchingExistingAction.consequences as any).filter((id) =>
        currentDeviationConsequenceIdSet.has(id)
      );
      const payloadConsequenceIds = Array.from(new Set([...existingIds, consequenceId]));

      dispatch(
        assignActionToConsequence({
          actionId: matchingActionId,
          consequenceId,
          deviationId: deviation.id ?? null,
          consequenceIds: payloadConsequenceIds,
        }) as any
      )
        .then((result: any) => {
          if (assignActionToConsequence.fulfilled.match(result)) {
            triggerNotification(
              'success',
              t('Recomendación asociada'),
              t('La recomendación existente se ha asociado correctamente'),
              'bottomRight'
            );
            setNewActionInputs((prev) => {
              const updated = { ...prev };
              delete updated[consequenceId];
              return updated;
            });
            if (deviation.id !== null && deviation.id !== undefined) {
              void dispatch(getDeviation(deviation.id) as any);
            }
            if (projectId) {
              void dispatch(getProjectActionsByProjectId(projectId) as any);
            }
          } else {
            const errorMessage = extractErrorMessage(result?.payload) ?? t('No se pudo asociar la recomendación');
            triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
          }
        })
        .catch((error: any) => {
          const errorMessage =
            extractErrorMessage(error?.response?.data ?? error?.error ?? error) ??
            t('No se pudo asociar la recomendación');
          triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
        });
      return;
    }

    const newAction: CreateActionType = {
      deviation: deviation.id ?? 0,
      name: trimmedName,
      responsability: current?.responsability || '',
      asociated_risk: current?.asociated_risk || '',
      consequences: [consequenceId],
      project_order: getNextProjectActionOrder(),
    };
    dispatch(createAction(newAction) as any)
      .then((result: any) => {
        if (createAction.fulfilled.match(result)) {
          triggerNotification('success', t('Acción creada'), t('La acción ha sido creada correctamente'), 'bottomRight');
          setNewActionInputs((prev) => {
            const updated = { ...prev };
            delete updated[consequenceId];
            return updated;
          });
        } else {
          const errorMessage = extractErrorMessage(result?.payload) ?? t('No se pudo crear la acción');
          triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
        }
      })
      .catch((error: any) => {
        const errorMessage =
          extractErrorMessage(error?.response?.data ?? error?.error ?? error) ?? t('No se pudo crear la acción');
        triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
      });
  };

  const handleCancelNewAction = (consequenceId: number) => {
    setNewActionInputs((prev) => {
      const updated = { ...prev };
      delete updated[consequenceId];
      return updated;
    });
  };

  const handleActionDelete = (record: ActionDeleteRecord) => {
    const normalizedActionId = normalizeActionId(record?.id ?? record?.actionId);
    if (normalizedActionId === null) {
      triggerNotification('error', t('Error'), t('No se pudo identificar la recomendación a eliminar'), 'bottomRight');
      return;
    }
    const resolvedConsequenceId = resolveConsequenceIdFromAction(record);
    onDelete('action', {
      ...record,
      id: normalizedActionId,
      consequenceId: resolvedConsequenceId,
      });
  };

  const handleCopyExistingConsequence = (causeId: number) => {
    const consequenceId = selectedExistingConsequenceIdByCause[causeId];
    if (!consequenceId) {
      return;
    }
    dispatch(copyConsequenceToCause({ consequenceId, causeId }) as any)
      .then((result: any) => {
        if (copyConsequenceToCause.fulfilled.match(result)) {
          triggerNotification(
            'success',
            t('Análisis copiado'),
            t('La consecuencia y su análisis se copiaron correctamente'),
            'bottomRight'
          );
          setExistingConsequenceSelectorCauseId(null);
          setSelectedExistingConsequenceIdByCause((prev) => ({ ...prev, [causeId]: null }));
          if (currentDeviationId) {
            void dispatch(getDeviation(currentDeviationId) as any);
          }
          if (projectId) {
            void dispatch(getProjectConsequencesByProjectId(projectId) as any);
            void dispatch(getProjectActionsByProjectId(projectId) as any);
          }
        } else {
          const errorMessage = extractErrorMessage(result?.payload) ?? t('No se pudo copiar el análisis');
          triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
        }
      })
      .catch((error: any) => {
        const errorMessage =
          extractErrorMessage(error?.response?.data ?? error) ?? t('No se pudo copiar el análisis');
        triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
      });
  };

  const handleMoveCause = (causeId: number | null | undefined, direction: 'up' | 'down') => {
    if (!causeId || !currentDeviationId) {
      return;
    }
    const thunk = direction === 'up' ? moveCauseUp : moveCauseDown;
    dispatch(thunk({ causeId, deviationId: currentDeviationId }) as any)
      .then((result: any) => {
        if (thunk.fulfilled.match(result)) {
          void dispatch(getDeviation(currentDeviationId) as any);
          if (projectId) {
            void dispatch(getProjectCausesByProjectId(projectId) as any);
          }
        } else {
          const errorMessage = extractErrorMessage(result?.payload) ?? t('No se pudo reordenar la causa');
          triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
        }
      })
      .catch((error: any) => {
        const errorMessage = extractErrorMessage(error?.response?.data ?? error) ?? t('No se pudo reordenar la causa');
        triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
      });
  };

  const handleMoveSafeguard = (safeguardId: number | null | undefined, direction: 'up' | 'down') => {
    if (!safeguardId || !currentDeviationId) {
      return;
    }
    const thunk = direction === 'up' ? moveSafeguardUp : moveSafeguardDown;
    dispatch(thunk(safeguardId) as any)
      .then((result: any) => {
        if (thunk.fulfilled.match(result)) {
          void dispatch(getDeviation(currentDeviationId) as any);
        } else {
          const errorMessage = extractErrorMessage(result?.payload) ?? t('No se pudo reordenar la salvaguarda');
          triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
        }
      })
      .catch((error: any) => {
        const errorMessage =
          extractErrorMessage(error?.response?.data ?? error) ?? t('No se pudo reordenar la salvaguarda');
        triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
      });
  };

  const handleMoveAction = (actionId: number | null | undefined, direction: 'up' | 'down') => {
    if (!actionId || !currentDeviationId) {
      return;
    }
    const thunk = direction === 'up' ? moveActionUp : moveActionDown;
    dispatch(thunk(actionId) as any)
      .then((result: any) => {
        if (thunk.fulfilled.match(result)) {
          void dispatch(getDeviation(currentDeviationId) as any);
          if (projectId) {
            void dispatch(getProjectActionsByProjectId(projectId) as any);
          }
        } else {
          const errorMessage = extractErrorMessage(result?.payload) ?? t('No se pudo reordenar la recomendación');
          triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
        }
      })
      .catch((error: any) => {
        const errorMessage =
          extractErrorMessage(error?.response?.data ?? error) ?? t('No se pudo reordenar la recomendación');
        triggerNotification('error', t('Error'), errorMessage, 'bottomRight');
      });
  };

  const handleUpdateConsequenceField = async (
    consequenceId: number | null | undefined,
    payload: Partial<UpdateConsequenceType>,
    currentName?: string
  ) => {
    if (consequenceId === null || consequenceId === undefined) {
      return;
    }

    try {
      const body: UpdateConsequenceType = {
        id: consequenceId,
        ...(currentName ? { name: currentName } : {}),
        ...payload,
      };
      const result = await dispatch(updateConsequence(body) as any);
      if (!updateConsequence.fulfilled.match(result)) {
        console.error('Error al actualizar la consecuencia');
      }
    } catch (error) {
      console.error('Error en la actualización de la consecuencia:', error);
    }
  };

  const handleMatrixSelectChange = async (consequence: ConsequenceType, newMatrixId: number | null): Promise<void> => {
    const consequenceId = consequence?.id;
    if (consequenceId === null || consequenceId === undefined) {
      return;
    }

    updateSelectionState(consequenceId, {
      matrixId: newMatrixId,
      potentialSeverity: null,
      potentialLikelihood: null,
      potentialRisk: null,
      mitigatedSeverity: null,
      mitigatedLikelihood: null,
      mitigatedRisk: null,
      projectedSeverity: null,
      projectedLikelihood: null,
      projectedRisk: null,
    });

    setMatrixIdByConsequence((prev) => ({
      ...prev,
      [consequenceId]: newMatrixId ?? null,
    }));

    if (newMatrixId) {
      await ensureMatrixDataLoaded(newMatrixId, consequenceId);
    }

    await handleUpdateConsequenceField(
      consequenceId,
      {
        potential_risk_matrix: newMatrixId,
        mitigated_risk_matrix: newMatrixId,
        projected_risk_matrix: newMatrixId,
        potential_risk_matrix_severity: null,
        potential_risk_matrix_likelihood: null,
        potential_risk_matrix_risk: null,
        mitigated_risk_matrix_severity: null,
        mitigated_risk_matrix_likelihood: null,
        mitigated_risk_matrix_risk: null,
        projected_risk_matrix_severity: null,
        projected_risk_matrix_likelihood: null,
        projected_risk_matrix_risk: null,
      },
      consequence.name
    );
  };

  const renderSharedStatus = ({
    isShared,
    isLinkedHere,
    referenceCode,
    referenceDeviationId,
    elementId,
  }: {
    isShared?: boolean;
    isLinkedHere?: boolean;
    referenceCode?: string | null;
    referenceDeviationId?: number | null;
    elementId?: string | null;
  }) => {
    if (!isShared && !isLinkedHere) {
      return null;
    }
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
        {isLinkedHere ? <Tag color='blue'>{t('Enlazada')}</Tag> : isShared ? <Tag color='gold'>{t('Compartida')}</Tag> : null}
        {isLinkedHere && referenceCode ? (
          <Button
            type='link'
            size='small'
            style={{ padding: 0, height: 'auto' }}
            onClick={() => navigateToReference(referenceDeviationId, elementId)}
          >
            {referenceCode}
          </Button>
        ) : null}
      </div>
    );
  };

  const columnsWithSpan: DeviationTableColumn[] = [
    {
      title: causesLabel,
      key: 'cause',
      onCell: (record: TableRow) => ({
        rowSpan: record.rowSpan ?? 0,
      }),
      render: (_: any, record?: TableRow) => {
        if (!record) {
          return null;
        }

        if ((record.rowSpan ?? 0) === 0) {
          return null;
        }

        if (record.isNewEditable) {
          const tempKey = Number(record.key);
          return (
            <TextArea
              value={record.name}
              placeholder={t('Nueva causa')}
              onChange={(e) => handleChangeCauseInput(e.target.value, tempKey)}
              onPressEnter={() => handleNewCauseSave(tempKey)}
              onBlur={() => handleNewCauseSave(tempKey)}
              autoFocus
              size='small'
              style={{ width: 200, display: 'block' }}
            />
          );
        }

        if (!record.causeId) {
          return null;
        }

        const baseName = record.causeName ?? '';
        const displayName = record.causeOrder ? `${record.causeOrder}. ${baseName}`.trim() : baseName;
        const causeData = (deviation?.causes ?? []).find((item) => item?.id === record.causeId);
        const isLinkedHere =
          !!causeData?.primary_deviation?.id && causeData.primary_deviation.id !== currentDeviationId;
        const causeElementId = record.causeId ? `deviation-cause-${record.causeId}` : null;

        if (editingItem.type === 'cause' && editingItem.id === record.causeId) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {record.causeOrder && <Text style={{ width: 200, display: 'block' }}>{displayName}</Text>}
              <TextArea
                value={editedFormValues.cause.name}
                onChange={(e) =>
                  setEditedFormValues((prev: any) => ({
                    ...prev,
                    cause: { ...prev.cause, name: e.target.value, id: record.causeId },
                  }))
                }
                onPressEnter={modifyCause}
                onBlur={modifyCause}
                autoFocus
                size='small'
                style={{ width: 200, display: 'block' }}
              />
            </div>
          );
        }

        return (
          <div id={causeElementId ?? undefined}>
            <Text style={{ width: 200, display: 'block' }}>{displayName}</Text>
            {renderSharedStatus({
              isShared: causeData?.is_shared,
              isLinkedHere,
              referenceCode: causeData?.reference_code,
              referenceDeviationId: causeData?.primary_deviation?.id ?? null,
              elementId: causeElementId,
            })}
            {(causeData?.is_shared || isLinkedHere) && renderSharedEditWarning()}
            {record.isFirstForCause && (
              <span style={{ justifyContent: 'flex-end', display: 'flex', gap: '5px' }}>
                <Tooltip title={t('Subir causa')}>
                  <Button
                    icon={<ArrowUpOutlined />}
                    onClick={() => handleMoveCause(record.causeId, 'up')}
                    size='small'
                    type='text'
                    disabled={(record.causeOrder ?? 0) <= 1}
                    aria-label={t('Subir causa')}
                  />
                </Tooltip>
                <Tooltip title={t('Bajar causa')}>
                  <Button
                    icon={<ArrowDownOutlined />}
                    onClick={() => handleMoveCause(record.causeId, 'down')}
                    size='small'
                    type='text'
                    disabled={(record.causeOrder ?? 0) >= sortedCauses.length}
                    aria-label={t('Bajar causa')}
                  />
                </Tooltip>
                <Button
                  icon={<EditOutlined />}
                  onClick={() =>
                    onEdit('cause', {
                      id: record.causeId,
                      name: record.causeName,
                    })
                  }
                  size='small'
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => {
                    if (!record.causeId || typeof record.causeId !== 'number') return;
                    dispatch(cloneCause(record.causeId) as any)
                      .then((result: any) => {
                        if (cloneCause.fulfilled.match(result)) {
                          triggerNotification(
                            'success',
                            t('Causa clonada'),
                            t('La causa ha sido clonada correctamente'),
                            'bottomRight'
                          );
                          if (deviation?.id) {
                            void dispatch(getDeviation(deviation.id) as any);
                          }
                        } else {
                          const message = extractErrorMessage(result.payload) ?? t('No se pudo clonar');
                          triggerNotification('error', t('Error'), message, 'bottomRight');
                        }
                      })
                      .catch((error: any) => {
                        const message = extractErrorMessage(error) ?? t('No se pudo clonar');
                        triggerNotification('error', t('Error'), message, 'bottomRight');
                      });
                  }}
                  size='small'
                />
                <Tooltip title={t('Eliminar causa')}>
                  <Button
                    icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                    onClick={() =>
                      onDelete('cause', {
                        key: record.causeId,
                        deviationId: currentDeviationId,
                      })
                    }
                    size='small'
                    type='text'
                    danger
                    aria-label={t('Eliminar causa')}
                  />
                </Tooltip>
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: consequencesLabel,
      key: 'consequence',
      render: (_: any, record?: TableRow) => {
        if (!record) {
          return null;
        }

        const causeId = record.causeId;
        const consequence = record.consequence;
        const showControls = record.isLastForCause && typeof causeId === 'number';

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {consequence ? (
              (() => {
                const isLinkedHere = !!consequence.primary_cause?.id && consequence.primary_cause.id !== causeId;
                return (
                  <div id={consequence.id ? `deviation-consequence-${consequence.id}` : undefined}>
                <ConsequenceRow
                  key={consequence.id}
                  data={consequence}
                  order={record.consequenceOrder}
                  onEdit={onEdit}
                  onDelete={(type, item) => onDelete(type, { ...item, causeId })}
                  editingItem={editingItem}
                  editedFormValues={editedFormValues}
                  setEditedFormValues={setEditedFormValues}
                  modifyConsequence={modifyConsequence}
                  setEditingItem={setEditingItem}
                  deviationId={typeof deviation?.id === 'number' ? deviation.id : undefined}
                />
                {renderSharedStatus({
                  isShared: consequence.is_shared,
                  isLinkedHere,
                  referenceCode: consequence.reference_code,
                  referenceDeviationId: consequence.reference_deviation_id,
                  elementId: consequence.id ? `deviation-consequence-${consequence.id}` : null,
                })}
                {(consequence.is_shared || isLinkedHere) && renderSharedEditWarning()}
              </div>
                );
              })()
            ) : (
              <Text type='secondary'>{t('Sin consecuencias registradas')}</Text>
            )}

            {showControls && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {existingConsequenceSelectorCauseId === causeId && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Select<number>
                      showSearch
                      style={{ flex: 1 }}
                      size='small'
                      placeholder={t('Buscar consecuencia del proyecto')}
                      value={selectedExistingConsequenceIdByCause[causeId] ?? undefined}
                      options={getExistingConsequenceOptions(causeId)}
                      loading={isLoadingProjectConsequences}
                      onChange={(value) =>
                        setSelectedExistingConsequenceIdByCause((prev) => ({ ...prev, [causeId]: value }))
                      }
                      filterOption={(input, option) =>
                        normalizeForSearch(String(option?.label ?? '')).includes(normalizeForSearch(input))
                      }
                    />
                    <Button size='small' type='primary' onClick={() => handleLinkExistingConsequence(causeId)}>
                      {t('Enlazar')}
                    </Button>
                    <Button size='small' onClick={() => handleCopyExistingConsequence(causeId)}>
                      {t('Copiar')}
                    </Button>
                  </div>
                )}
                {newConsequenceInputs[causeId] !== undefined && (
                  <TextArea
                    value={newConsequenceInputs[causeId] || ''}
                    placeholder={t('Nueva consecuencia')}
                    size='small'
                    autoFocus
                    onChange={(e) => handleConsequenceInputChange(causeId, e.target.value)}
                    onPressEnter={() => handleNewConsequenceSave(causeId)}
                    onBlur={() => {
                      handleNewConsequenceSave(causeId);
                    }}
                  />
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    icon={<PlusCircleOutlined />}
                    onClick={() => handleAddConsequenceInput(causeId)}
                    size='small'
                    disabled={newConsequenceInputs[causeId] !== undefined}
                  >
                    {t('Añadir')}
                  </Button>
                  <Button
                    icon={<LinkOutlined />}
                    onClick={() =>
                      setExistingConsequenceSelectorCauseId((prev) => (prev === causeId ? null : causeId))
                    }
                    size='small'
                  >
                    {t('Usar existente')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('Cat'),
      key: 'category',
      render: (_: any, record?: TableRow) => {
        if (!record) return null;
        const consequence = record?.consequence;
        const consequenceId = consequence?.id;
        const hasConsequenceId = consequenceId !== null && consequenceId !== undefined;
        const currentMatrixId = hasConsequenceId ? resolveMatrixIdForConsequence(consequence) : null;

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Select<number>
              value={hasConsequenceId ? currentMatrixId ?? undefined : undefined}
              onChange={(value) => {
                const parsedValue = getRelationId(value);
                if (hasConsequenceId && consequence) {
                  void handleMatrixSelectChange(consequence, parsedValue);
                }
              }}
              className='matrix-select'
              disabled={!hasConsequenceId}
              placeholder={t('Selecciona matriz')}
              size='small'
              options={toNumericSelectOptions(matricesAvailable)}
            />
          </div>
        );
      },
    },
    {
      title: t('Riesgo Potencial'),
      key: 'potentialRiskGroup',
      children: [
        {
          title: t('S'),
          key: 'potentialSeverity',
          render: (_: any, record?: TableRow) => {
            const consequence = record?.consequence;
            const consequenceId = consequence?.id;
            if (!consequence || consequenceId === null || consequenceId === undefined) {
              return <span>-</span>;
            }

            const matrixId = resolveMatrixIdForConsequence(consequence);

            if (!matrixId) {
              return <span>-</span>;
            }

            if (loadingSeverityLevels[matrixId]) {
              return <span>{t('Cargando...')}</span>;
            }

            const severityLevels = severityLevelsByMatrix[matrixId] || [];
            if (!severityLevels.length) {
              return <span>-</span>;
            }

            const selection = getSelectionState(consequenceId);
            const currentValue =
              selection.potentialSeverity ?? getRelationId(consequence.potential_risk_matrix_severity) ?? null;

            return renderLevelSelectWithTooltip({
              value: currentValue,
              items: severityLevels,
              placeholder: t('- Elige -'),
              onChange: async (value) => {
                  const parsedValue = getRelationId(value);
                  const resolvedMatrixId = resolveMatrixIdForConsequence(consequence);

                  if (resolvedMatrixId) {
                    await ensureMatrixDataLoaded(resolvedMatrixId, consequenceId);
                  }

                  const selectionBefore = getSelectionState(consequenceId);
                  const potentialLikelihood =
                    selectionBefore.potentialLikelihood ?? getRelationId(consequence.potential_risk_matrix_likelihood);
                  let potentialRisk: MatrixRiskType | null = null;
                  if (resolvedMatrixId && parsedValue !== null && potentialLikelihood !== null) {
                    potentialRisk = findRiskLevel(resolvedMatrixId, parsedValue, potentialLikelihood);
                  }

                  updateSelectionState(consequenceId, {
                    matrixId: resolvedMatrixId ?? null,
                    potentialSeverity: parsedValue,
                    potentialRisk: parsedValue !== null ? potentialRisk : null,
                  });

                  const updates: Partial<UpdateConsequenceType> = {
                    potential_risk_matrix_severity: parsedValue,
                  };

                  if (parsedValue === null) {
                    updates.potential_risk_matrix_risk = null;
                  } else if (potentialRisk) {
                    updates.potential_risk_matrix_risk = potentialRisk.id ?? null;
                  } else if (potentialLikelihood !== null) {
                    updates.potential_risk_matrix_risk = null;
                  }

                  if (parsedValue !== null) {
                    const mitigatedExisting =
                      selectionBefore.mitigatedSeverity ?? getRelationId(consequence.mitigated_risk_matrix_severity);
                    if (mitigatedExisting === null) {
                      const mitigatedLikelihood =
                        selectionBefore.mitigatedLikelihood ??
                        getRelationId(consequence.mitigated_risk_matrix_likelihood);
                      let mitigatedRisk: MatrixRiskType | null = null;
                      if (resolvedMatrixId && mitigatedLikelihood !== null) {
                        mitigatedRisk = findRiskLevel(resolvedMatrixId, parsedValue, mitigatedLikelihood);
                      }
                      updates.mitigated_risk_matrix_severity = parsedValue;
                      if (mitigatedRisk) {
                        updates.mitigated_risk_matrix_risk = mitigatedRisk.id ?? null;
                      } else if (mitigatedLikelihood !== null) {
                        updates.mitigated_risk_matrix_risk = null;
                      }
                      updateSelectionState(consequenceId, {
                        mitigatedSeverity: parsedValue,
                        mitigatedRisk: mitigatedRisk,
                      });
                    }

                    const projectedExisting =
                      selectionBefore.projectedSeverity ?? getRelationId(consequence.projected_risk_matrix_severity);
                    if (projectedExisting === null) {
                      const projectedLikelihood =
                        selectionBefore.projectedLikelihood ??
                        getRelationId(consequence.projected_risk_matrix_likelihood);
                      let projectedRisk: MatrixRiskType | null = null;
                      if (resolvedMatrixId && projectedLikelihood !== null) {
                        projectedRisk = findRiskLevel(resolvedMatrixId, parsedValue, projectedLikelihood);
                      }
                      updates.projected_risk_matrix_severity = parsedValue;
                      if (projectedRisk) {
                        updates.projected_risk_matrix_risk = projectedRisk.id ?? null;
                      } else if (projectedLikelihood !== null) {
                        updates.projected_risk_matrix_risk = null;
                      }
                      updateSelectionState(consequenceId, {
                        projectedSeverity: parsedValue,
                        projectedRisk: projectedRisk,
                      });
                    }
                  }

                  await handleUpdateConsequenceField(consequenceId, updates, consequence.name);
                },
            });
          },
        },
        {
          title: t('UL'),
          key: 'potentialLikelihood',
          render: (_: any, record?: TableRow) => {
            const consequence = record?.consequence;
            const consequenceId = consequence?.id;
            if (!consequence || consequenceId === null || consequenceId === undefined) {
              return <span>-</span>;
            }

            const matrixId = resolveMatrixIdForConsequence(consequence);

            if (!matrixId) {
              return <span>-</span>;
            }

            if (loadingLikelihoodLevels[matrixId]) {
              return <span>{t('Cargando...')}</span>;
            }

            const likelihoodLevels = likelihoodLevelsByMatrix[matrixId] || [];
            if (!likelihoodLevels.length) {
              return <span>-</span>;
            }

            const selection = getSelectionState(consequenceId);
            const currentValue =
              selection.potentialLikelihood ?? getRelationId(consequence.potential_risk_matrix_likelihood) ?? null;

            return renderLevelSelectWithTooltip({
              value: currentValue,
              items: likelihoodLevels,
              placeholder: t('- Elige -'),
              onChange: async (value) => {
                  const parsedValue = getRelationId(value);
                  const resolvedMatrixId = resolveMatrixIdForConsequence(consequence);

                  if (resolvedMatrixId) {
                    await ensureMatrixDataLoaded(resolvedMatrixId, consequenceId);
                  }

                  const selectionBefore = getSelectionState(consequenceId);
                  const potentialSeverity =
                    selectionBefore.potentialSeverity ?? getRelationId(consequence.potential_risk_matrix_severity);
                  let potentialRisk: MatrixRiskType | null = null;
                  if (resolvedMatrixId && parsedValue !== null && potentialSeverity !== null) {
                    potentialRisk = findRiskLevel(resolvedMatrixId, potentialSeverity, parsedValue);
                  }

                  updateSelectionState(consequenceId, {
                    matrixId: resolvedMatrixId ?? null,
                    potentialLikelihood: parsedValue,
                    potentialRisk: parsedValue !== null ? potentialRisk : null,
                  });

                  const updates: Partial<UpdateConsequenceType> = {
                    potential_risk_matrix_likelihood: parsedValue,
                  };

                  if (parsedValue === null) {
                    updates.potential_risk_matrix_risk = null;
                  } else if (potentialRisk) {
                    updates.potential_risk_matrix_risk = potentialRisk.id ?? null;
                  } else if (potentialSeverity !== null) {
                    updates.potential_risk_matrix_risk = null;
                  }

                  if (parsedValue !== null) {
                    const mitigatedExisting =
                      selectionBefore.mitigatedLikelihood ??
                      getRelationId(consequence.mitigated_risk_matrix_likelihood);
                    if (mitigatedExisting === null) {
                      const mitigatedSeverity =
                        selectionBefore.mitigatedSeverity ?? getRelationId(consequence.mitigated_risk_matrix_severity);
                      let mitigatedRisk: MatrixRiskType | null = null;
                      if (resolvedMatrixId && mitigatedSeverity !== null) {
                        mitigatedRisk = findRiskLevel(resolvedMatrixId, mitigatedSeverity, parsedValue);
                      }
                      updates.mitigated_risk_matrix_likelihood = parsedValue;
                      if (mitigatedRisk) {
                        updates.mitigated_risk_matrix_risk = mitigatedRisk.id ?? null;
                      } else if (mitigatedSeverity !== null) {
                        updates.mitigated_risk_matrix_risk = null;
                      }
                      updateSelectionState(consequenceId, {
                        mitigatedLikelihood: parsedValue,
                        mitigatedRisk: mitigatedRisk,
                      });
                    }

                    const projectedExisting =
                      selectionBefore.projectedLikelihood ??
                      getRelationId(consequence.projected_risk_matrix_likelihood);
                    if (projectedExisting === null) {
                      const projectedSeverity =
                        selectionBefore.projectedSeverity ?? getRelationId(consequence.projected_risk_matrix_severity);
                      let projectedRisk: MatrixRiskType | null = null;
                      if (resolvedMatrixId && projectedSeverity !== null) {
                        projectedRisk = findRiskLevel(resolvedMatrixId, projectedSeverity, parsedValue);
                      }
                      updates.projected_risk_matrix_likelihood = parsedValue;
                      if (projectedRisk) {
                        updates.projected_risk_matrix_risk = projectedRisk.id ?? null;
                      } else if (projectedSeverity !== null) {
                        updates.projected_risk_matrix_risk = null;
                      }
                      updateSelectionState(consequenceId, {
                        projectedLikelihood: parsedValue,
                        projectedRisk: projectedRisk,
                      });
                    }
                  }

                  await handleUpdateConsequenceField(consequenceId, updates, consequence.name);
                },
            });
          },
        },
        {
          title: t('UR'),
          key: 'potentialRisk',
          onCell: (record: TableRow) => {
            const displayData = getRiskDisplayData(record?.consequence, 'potential');
            return {
              className: 'risk-color-cell',
              style: { backgroundColor: displayData.backgroundColor },
            };
          },
          render: (_: any, record?: TableRow) => {
            const displayData = getRiskDisplayData(record?.consequence, 'potential');
            return renderRiskBadge(displayData);
          },
        },
      ],
    },
    {
      title: safeguardsLabel,
      key: 'safeguards',
      render: (_: any, record?: TableRow) => {
        const consequence = record?.consequence;
        const consequenceId = consequence?.id;
        if (!consequence || consequenceId === null || consequenceId === undefined) {
          return <span>-</span>;
        }

        const safeguards = [...(consequence.safeguards ?? [])].sort((a, b) => compareByOrderThenId(a, b, 'order'));

        return (
          <div>
            {safeguards.map((sg, index) => (
              <SafeguardRow
                key={sg.id ?? `safeguard-${consequenceId}-${index}`}
                data={sg}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
                editingItem={editingItem}
                editedFormValues={editedFormValues}
                setEditedFormValues={setEditedFormValues}
                modifySafeguard={modifySafeguard}
                setEditingItem={setEditingItem}
                onMoveUp={(id) => handleMoveSafeguard(id, 'up')}
                onMoveDown={(id) => handleMoveSafeguard(id, 'down')}
                isFirst={index === 0}
                isLast={index === safeguards.length - 1}
              />
            ))}

            {newSafeguardInputs[consequenceId] !== undefined && (
              <div style={{ marginBottom: 8 }}>
                <AutoComplete
                  style={{ width: '100%' }}
                  options={safeguardAutoCompleteOptions}
                  onSelect={(value) => handleSafeguardAutocompleteSelect(consequenceId, value)}
                  filterOption={(inputValue, option) => {
                    const optionValue = typeof option?.value === 'string' ? option.value : '';
                    return optionValue.toLowerCase().includes(inputValue.toLowerCase());
                  }}
                >
                  <TextArea
                    value={newSafeguardInputs[consequenceId] || ''}
                    placeholder={t('Nueva salvaguarda')}
                    size='small'
                    autoFocus
                    onChange={(e) => handleSafeguardInputChange(consequenceId, e.target.value)}
                    onPressEnter={() => handleNewSafeguardSave(consequenceId)}
                    onBlur={() => handleNewSafeguardSave(consequenceId)}
                  />
                </AutoComplete>
              </div>
            )}

            <Button
              icon={<PlusCircleOutlined />}
              onClick={() => handleAddSafeguardInput(consequenceId)}
              size='small'
              disabled={newSafeguardInputs[consequenceId] !== undefined}
            >
              {t('Añadir')}
            </Button>
          </div>
        );
      },
    },
    {
      title: t('Riesgo Mitigado'),
      key: 'mitigatedRiskGroup',
      children: [
        {
          title: (
            <Tooltip title={showMitigatedSeverity ? t('Ocultar severidad mitigada') : t('Mostrar severidad mitigada')}>
              <span
                role='button'
                tabIndex={0}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleMitigatedSeverityVisibility();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleMitigatedSeverityVisibility();
                  }
                }}
                aria-label={showMitigatedSeverity ? t('Ocultar severidad mitigada') : t('Mostrar severidad mitigada')}
                style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
              >
                {showMitigatedSeverity ? (
                  <>
                    {'S '}
                    <EyeOutlined />
                  </>
                ) : (
                  <>
                    <EyeInvisibleOutlined />
                  </>
                )}
              </span>
            </Tooltip>
          ),
          key: 'mitigatedSeverity',
          render: (_: any, record?: TableRow) => {
            if (!showMitigatedSeverity) {
              return null;
            }

            const consequence = record?.consequence;
            const consequenceId = consequence?.id;
            if (!consequence || consequenceId === null || consequenceId === undefined) {
              return <span>-</span>;
            }

            const matrixId = resolveMatrixIdForConsequence(consequence);

            if (!matrixId) {
              return <span>-</span>;
            }

            if (loadingSeverityLevels[matrixId]) {
              return <span>{t('Cargando...')}</span>;
            }

            const severityLevels = severityLevelsByMatrix[matrixId] || [];
            if (!severityLevels.length) {
              return <span>-</span>;
            }

            const selection = getSelectionState(consequenceId);
            const currentValue =
              selection.mitigatedSeverity ?? getRelationId(consequence.mitigated_risk_matrix_severity) ?? null;

            return renderLevelSelectWithTooltip({
              value: currentValue,
              items: severityLevels,
              placeholder: t('- Elige -'),
              onChange: async (value) => {
                  const parsedValue = getRelationId(value);
                  const resolvedMatrixId = resolveMatrixIdForConsequence(consequence);

                  if (resolvedMatrixId) {
                    await ensureMatrixDataLoaded(resolvedMatrixId, consequenceId);
                  }

                  const selectionBefore = getSelectionState(consequenceId);
                  const mitigatedLikelihood =
                    selectionBefore.mitigatedLikelihood ?? getRelationId(consequence.mitigated_risk_matrix_likelihood);
                  let mitigatedRisk: MatrixRiskType | null = null;
                  if (resolvedMatrixId && parsedValue !== null && mitigatedLikelihood !== null) {
                    mitigatedRisk = findRiskLevel(resolvedMatrixId, parsedValue, mitigatedLikelihood);
                  }

                  updateSelectionState(consequenceId, {
                    matrixId: resolvedMatrixId ?? null,
                    mitigatedSeverity: parsedValue,
                    mitigatedRisk: parsedValue !== null ? mitigatedRisk : null,
                  });

                  const updates: Partial<UpdateConsequenceType> = {
                    mitigated_risk_matrix_severity: parsedValue,
                  };

                  if (parsedValue === null) {
                    updates.mitigated_risk_matrix_risk = null;
                  } else if (mitigatedRisk) {
                    updates.mitigated_risk_matrix_risk = mitigatedRisk.id ?? null;
                  } else if (mitigatedLikelihood !== null) {
                    updates.mitigated_risk_matrix_risk = null;
                  }

                  await handleUpdateConsequenceField(consequenceId, updates, consequence.name);
                },
            });
          },
        },
        {
          title: t('ML'),
          key: 'mitigatedLikelihood',
          render: (_: any, record?: TableRow) => {
            const consequence = record?.consequence;
            const consequenceId = consequence?.id;
            if (!consequence || consequenceId === null || consequenceId === undefined) {
              return <span>-</span>;
            }

            const matrixId = resolveMatrixIdForConsequence(consequence);

            if (!matrixId) {
              return <span>-</span>;
            }

            if (loadingLikelihoodLevels[matrixId]) {
              return <span>{t('Cargando...')}</span>;
            }

            const likelihoodLevels = likelihoodLevelsByMatrix[matrixId] || [];
            if (!likelihoodLevels.length) {
              return <span>-</span>;
            }

            const selection = getSelectionState(consequenceId);
            const currentValue =
              selection.mitigatedLikelihood ?? getRelationId(consequence.mitigated_risk_matrix_likelihood) ?? null;

            return renderLevelSelectWithTooltip({
              value: currentValue,
              items: likelihoodLevels,
              placeholder: t('- Elige -'),
              onChange: async (value) => {
                  const parsedValue = getRelationId(value);
                  const resolvedMatrixId = resolveMatrixIdForConsequence(consequence);

                  if (resolvedMatrixId) {
                    await ensureMatrixDataLoaded(resolvedMatrixId, consequenceId);
                  }

                  const selectionBefore = getSelectionState(consequenceId);
                  const mitigatedSeverity =
                    selectionBefore.mitigatedSeverity ?? getRelationId(consequence.mitigated_risk_matrix_severity);
                  let mitigatedRisk: MatrixRiskType | null = null;
                  if (resolvedMatrixId && parsedValue !== null && mitigatedSeverity !== null) {
                    mitigatedRisk = findRiskLevel(resolvedMatrixId, mitigatedSeverity, parsedValue);
                  }

                  updateSelectionState(consequenceId, {
                    matrixId: resolvedMatrixId ?? null,
                    mitigatedLikelihood: parsedValue,
                    mitigatedRisk: parsedValue !== null ? mitigatedRisk : null,
                  });

                  const updates: Partial<UpdateConsequenceType> = {
                    mitigated_risk_matrix_likelihood: parsedValue,
                  };

                  if (parsedValue === null) {
                    updates.mitigated_risk_matrix_risk = null;
                  } else if (mitigatedRisk) {
                    updates.mitigated_risk_matrix_risk = mitigatedRisk.id ?? null;
                  } else if (mitigatedSeverity !== null) {
                    updates.mitigated_risk_matrix_risk = null;
                  }

                  await handleUpdateConsequenceField(consequenceId, updates, consequence.name);
                },
            });
          },
        },
        {
          title: t('MR'),
          key: 'mitigatedRisk',
          onCell: (record: TableRow) => {
            const displayData = getRiskDisplayData(record?.consequence, 'mitigated');
            return {
              className: 'risk-color-cell',
              style: { backgroundColor: displayData.backgroundColor },
            };
          },
          render: (_: any, record?: TableRow) => {
            const displayData = getRiskDisplayData(record?.consequence, 'mitigated');
            return renderRiskBadge(displayData);
          },
        },
      ],
    },
    {
      title: recommendationsLabel,
      key: 'actions',
      render: (_: any, record?: TableRow) => {
        const consequence = record?.consequence;
        const consequenceId = consequence?.id;
        if (!consequence || consequenceId === null || consequenceId === undefined) {
          return <span>-</span>;
        }

        const actions = [...(consequence.actions ?? [])].sort((a, b) => compareByOrderThenId(a, b, 'project_order'));

        return (
          <div>
            {actions.map((rec, index) => {
              const normalizedConsequences = toNumericConsequenceIds(rec.consequences as any);
              const normalizedActionId = normalizeActionId(
                rec?.id ?? (rec as any)?.actionId ?? (rec as any)?.action_id
              );
              const actionKey = normalizedActionId ?? `consequence-${consequenceId}-action-${index}`;
              return (
                <ActionRow
                  key={actionKey}
                  data={{
                    ...rec,
                    id: normalizedActionId,
                    consequenceId: consequenceId ?? null,
                    consequences: normalizedConsequences,
                  }}
                  onEdit={onEdit}
                  onDelete={(_type, payload) => handleActionDelete(payload as ActionDeleteRecord)}
                  editingItem={editingItem}
                  editedFormValues={editedFormValues}
                  setEditedFormValues={setEditedFormValues}
                  modifyAction={modifyAction}
                  setEditingItem={setEditingItem}
                  onMoveUp={(id) => handleMoveAction(id, 'up')}
                  onMoveDown={(id) => handleMoveAction(id, 'down')}
                  isFirst={index === 0}
                  isLast={index === actions.length - 1}
                />
              );
            })}

            {newActionInputs[consequenceId] !== undefined && (
              <div style={{ marginBottom: 8, display: 'flex', gap: 4, flexDirection: 'column' }}>
                <AutoComplete
                  style={{ width: 200 }}
                  options={actionAutoCompleteOptions}
                  value={newActionInputs[consequenceId]?.name || ''}
                  onSelect={(_, option) =>
                    handleActionAutocompleteSelect(consequence, option as ActionAutoCompleteOption)
                  }
                  filterOption={(inputValue, option) => {
                    const optionWithSearch = option as ActionAutoCompleteOption;
                    const normalizedInput = normalizeForSearch(inputValue);
                    if (optionWithSearch?.searchValue) {
                      return optionWithSearch.searchValue.includes(normalizedInput);
                    }
                    const fallbackTargets = [
                      typeof option?.value === 'string' ? normalizeForSearch(option.value) : '',
                      normalizeForSearch(optionWithSearch?.name ?? ''),
                    ].filter(Boolean);
                    return fallbackTargets.some((target) => target.includes(normalizedInput));
                  }}
                >
                  <TextArea
                    value={newActionInputs[consequenceId]?.name || ''}
                    placeholder={t('Nueva acción')}
                    size='small'
                    autoFocus
                    onChange={(e) => handleActionInputChange(consequenceId, 'name', e.target.value)}
                  />
                </AutoComplete>
                {newActionInputs[consequenceId]?.isExisting &&
                  newActionInputs[consequenceId]?.project_order !== null && (
                    <Text type='secondary'>
                      {t('Orden en proyecto: {{order}}', { order: newActionInputs[consequenceId]?.project_order })}
                    </Text>
                  )}
                <small style={{ fontWeight: 'bold', marginTop: 20 }}>{t('Responsable')}</small>
                <TextArea
                  value={newActionInputs[consequenceId]?.responsability || ''}
                  placeholder={t('Responsable')}
                  size='small'
                  onChange={(e) => handleActionInputChange(consequenceId, 'responsability', e.target.value)}
                />
                <small style={{ fontWeight: 'bold' }}>{t('Riesgo asociado')}</small>
                <TextArea
                  value={newActionInputs[consequenceId]?.asociated_risk || ''}
                  placeholder={t('Riesgo asociado')}
                  size='small'
                  onChange={(e) => handleActionInputChange(consequenceId, 'asociated_risk', e.target.value)}
                />
                <Button type='primary' onClick={() => handleNewActionSave(consequenceId)}>
                  {t('Guardar Recomendación')}
                </Button>
                <Button onClick={() => handleCancelNewAction(consequenceId)}>{t('Cancelar')}</Button>
              </div>
            )}

            <Button
              icon={<PlusCircleOutlined />}
              onClick={() => handleAddActionInput(consequenceId)}
              size='small'
              disabled={newActionInputs[consequenceId] !== undefined}
            >
              {t('Añadir')}
            </Button>
          </div>
        );
      },
    },
    {
      title: t('Riesgo Proyectado'),
      key: 'projectedRiskGroup',
      children: [
        {
          title: (
            <Tooltip title={showProjectedSeverity ? t('Ocultar severidad proyectada') : t('Mostrar severidad proyectada')}>
              <span
                role='button'
                tabIndex={0}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleProjectedSeverityVisibility();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleProjectedSeverityVisibility();
                  }
                }}
                aria-label={showProjectedSeverity ? t('Ocultar severidad proyectada') : t('Mostrar severidad proyectada')}
                style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
              >
                {showProjectedSeverity ? (
                  <>
                    {'S '}
                    <EyeOutlined />
                  </>
                ) : (
                  <>
                    <EyeInvisibleOutlined />
                  </>
                )}
              </span>
            </Tooltip>
          ),
          key: 'projectedSeverity',
          render: (_: any, record?: TableRow) => {
            if (!showProjectedSeverity) {
              return null;
            }

            const consequence = record?.consequence;
            const consequenceId = consequence?.id;
            if (!consequence || consequenceId === null || consequenceId === undefined) {
              return <span>-</span>;
            }

            const matrixId = resolveMatrixIdForConsequence(consequence);

            if (!matrixId) {
              return <span>-</span>;
            }

            if (loadingSeverityLevels[matrixId]) {
              return <span>{t('Cargando...')}</span>;
            }

            const severityLevels = severityLevelsByMatrix[matrixId] || [];
            if (!severityLevels.length) {
              return <span>-</span>;
            }

            const selection = getSelectionState(consequenceId);
            const currentValue =
              selection.projectedSeverity ?? getRelationId(consequence.projected_risk_matrix_severity) ?? null;

            return renderLevelSelectWithTooltip({
              value: currentValue,
              items: severityLevels,
              placeholder: t('- Elige -'),
              onChange: async (value) => {
                  const parsedValue = getRelationId(value);
                  const resolvedMatrixId = resolveMatrixIdForConsequence(consequence);

                  if (resolvedMatrixId) {
                    await ensureMatrixDataLoaded(resolvedMatrixId, consequenceId);
                  }

                  const selectionBefore = getSelectionState(consequenceId);
                  const projectedLikelihood =
                    selectionBefore.projectedLikelihood ?? getRelationId(consequence.projected_risk_matrix_likelihood);
                  let projectedRisk: MatrixRiskType | null = null;
                  if (resolvedMatrixId && parsedValue !== null && projectedLikelihood !== null) {
                    projectedRisk = findRiskLevel(resolvedMatrixId, parsedValue, projectedLikelihood);
                  }

                  updateSelectionState(consequenceId, {
                    matrixId: resolvedMatrixId ?? null,
                    projectedSeverity: parsedValue,
                    projectedRisk: parsedValue !== null ? projectedRisk : null,
                  });

                  const updates: Partial<UpdateConsequenceType> = {
                    projected_risk_matrix_severity: parsedValue,
                  };

                  if (parsedValue === null) {
                    updates.projected_risk_matrix_risk = null;
                  } else if (projectedRisk) {
                    updates.projected_risk_matrix_risk = projectedRisk.id ?? null;
                  } else if (projectedLikelihood !== null) {
                    updates.projected_risk_matrix_risk = null;
                  }

                  await handleUpdateConsequenceField(consequenceId, updates, consequence.name);
                },
            });
          },
        },
        {
          title: t('PL'),
          key: 'projectedLikelihood',
          render: (_: any, record?: TableRow) => {
            const consequence = record?.consequence;
            const consequenceId = consequence?.id;
            if (!consequence || consequenceId === null || consequenceId === undefined) {
              return <span>-</span>;
            }

            const matrixId = resolveMatrixIdForConsequence(consequence);

            if (!matrixId) {
              return <span>-</span>;
            }

            if (loadingLikelihoodLevels[matrixId]) {
              return <span>{t('Cargando...')}</span>;
            }

            const likelihoodLevels = likelihoodLevelsByMatrix[matrixId] || [];
            if (!likelihoodLevels.length) {
              return <span>-</span>;
            }

            const selection = getSelectionState(consequenceId);
            const currentValue =
              selection.projectedLikelihood ?? getRelationId(consequence.projected_risk_matrix_likelihood) ?? null;

            return renderLevelSelectWithTooltip({
              value: currentValue,
              items: likelihoodLevels,
              placeholder: t('- Elige -'),
              onChange: async (value) => {
                  const parsedValue = getRelationId(value);
                  const resolvedMatrixId = resolveMatrixIdForConsequence(consequence);

                  if (resolvedMatrixId) {
                    await ensureMatrixDataLoaded(resolvedMatrixId, consequenceId);
                  }

                  const selectionBefore = getSelectionState(consequenceId);
                  const projectedSeverity =
                    selectionBefore.projectedSeverity ?? getRelationId(consequence.projected_risk_matrix_severity);
                  let projectedRisk: MatrixRiskType | null = null;
                  if (resolvedMatrixId && parsedValue !== null && projectedSeverity !== null) {
                    projectedRisk = findRiskLevel(resolvedMatrixId, projectedSeverity, parsedValue);
                  }

                  updateSelectionState(consequenceId, {
                    matrixId: resolvedMatrixId ?? null,
                    projectedLikelihood: parsedValue,
                    projectedRisk: parsedValue !== null ? projectedRisk : null,
                  });

                  const updates: Partial<UpdateConsequenceType> = {
                    projected_risk_matrix_likelihood: parsedValue,
                  };

                  if (parsedValue === null) {
                    updates.projected_risk_matrix_risk = null;
                  } else if (projectedRisk) {
                    updates.projected_risk_matrix_risk = projectedRisk.id ?? null;
                  } else if (projectedSeverity !== null) {
                    updates.projected_risk_matrix_risk = null;
                  }

                  await handleUpdateConsequenceField(consequenceId, updates, consequence.name);
                },
            });
          },
        },
        {
          title: t('PR'),
          key: 'projectedRisk',
          onCell: (record: TableRow) => {
            const displayData = getRiskDisplayData(record?.consequence, 'projected');
            return {
              className: 'risk-color-cell',
              style: { backgroundColor: displayData.backgroundColor },
            };
          },
          render: (_: any, record?: TableRow) => {
            const displayData = getRiskDisplayData(record?.consequence, 'projected');
            return renderRiskBadge(displayData);
          },
        },
      ],
    },
  ];
  const visibleColumns = columnsWithSpan.filter((column) => {
    if (column.key === 'category') {
      return showRiskAnalysis;
    }
    if (column.key === 'potentialRiskGroup' || column.key === 'mitigatedRiskGroup' || column.key === 'projectedRiskGroup') {
      return showRiskAnalysis;
    }
    if (column.key === 'safeguards') {
      return showSafeguards;
    }
    if (column.key === 'actions') {
      return showRecommendations;
    }
    return true;
  });
  // Construcción del dataSource con filas nuevas editables y las causas existentes
  const dataSourceWithSpan: TableRow[] = [
    ...newCauses.map((c) => ({
      key: c.key,
      name: c.name,
      isNewEditable: true,
      rowSpan: 1,
    })),
  ];

  sortedCauses.forEach((cause, causeIndex) => {
    const causeId = cause.id as number;
    const causeOrder = causeIndex + 1;
    const aggregated = [...(consequencesByCauseId[causeId] ?? []), ...(cause.consequences ?? [])];

    const uniqueConsequences: ConsequenceType[] = [];
    const seenIds = new Set<number>();

    aggregated.forEach((consequence) => {
      const consequenceId = consequence.id;
      if (typeof consequenceId === 'number') {
        if (seenIds.has(consequenceId)) {
          const index = uniqueConsequences.findIndex((item) => item.id === consequenceId);
          if (index >= 0) {
            const existingConsequence = uniqueConsequences[index];
            const mergedActions = mergeItemsById(existingConsequence.actions, consequence.actions);
            const mergedSafeguards = mergeItemsById(existingConsequence.safeguards, consequence.safeguards);
            const mergedCauses = mergeItemsById(existingConsequence.causes, consequence.causes);

            uniqueConsequences[index] = {
              ...existingConsequence,
              ...consequence,
              actions: mergedActions.length
                ? mergedActions
                : consequence.actions ?? existingConsequence.actions ?? undefined,
              safeguards: mergedSafeguards.length
                ? mergedSafeguards
                : consequence.safeguards ?? existingConsequence.safeguards ?? undefined,
              causes: mergedCauses.length
                ? mergedCauses
                : consequence.causes ?? existingConsequence.causes ?? undefined,
            };
          }
        } else {
          seenIds.add(consequenceId);
          uniqueConsequences.push({ ...consequence });
        }
      } else {
        uniqueConsequences.push({ ...consequence });
      }
    });

    const rowCount = uniqueConsequences.length || 1;

    uniqueConsequences.sort((a, b) => {
      const createdA = a.created_at ? Date.parse(a.created_at) : null;
      const createdB = b.created_at ? Date.parse(b.created_at) : null;
      const hasCreatedA = typeof createdA === 'number' && !Number.isNaN(createdA);
      const hasCreatedB = typeof createdB === 'number' && !Number.isNaN(createdB);

      if (hasCreatedA && hasCreatedB && createdA !== createdB) {
        return createdA - createdB;
      }

      if (hasCreatedA && !hasCreatedB) {
        return -1;
      }

      if (!hasCreatedA && hasCreatedB) {
        return 1;
      }

      const idA = typeof a.id === 'number' ? a.id : Number.MAX_SAFE_INTEGER;
      const idB = typeof b.id === 'number' ? b.id : Number.MAX_SAFE_INTEGER;

      if (idA !== idB) {
        return idA - idB;
      }

      return 0;
    });

    if (uniqueConsequences.length === 0) {
      dataSourceWithSpan.push({
        key: `cause-${causeId}-placeholder`,
        causeId,
        causeName: cause.name ?? '',
        causeOrder,
        consequence: null,
        isFirstForCause: true,
        isLastForCause: true,
        isPlaceholder: true,
        rowSpan: rowCount,
      });
    } else {
      uniqueConsequences.forEach((consequence, index) => {
        dataSourceWithSpan.push({
          key: consequence.id ?? `cause-${causeId}-consequence-${index}`,
          causeId,
          causeName: cause.name ?? '',
          causeOrder,
          consequence,
          consequenceOrder: index + 1,
          isFirstForCause: index === 0,
          isLastForCause: index === uniqueConsequences.length - 1,
          rowSpan: index === 0 ? rowCount : 0,
        });
      });
    }
  });
  return (
    <div className='deviation-table-wrapper'>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button icon={<PlusCircleOutlined />} onClick={handleAddNewCauseRow} type='primary' size='small'>
          {t('Añadir causa')}
        </Button>
        <Button
          icon={<LinkOutlined />}
          onClick={() => setShowExistingCauseSelector((prev) => !prev)}
          size='small'
        >
          {t('Usar existente')}
        </Button>
        {showExistingCauseSelector && (
          <>
            <Select<number>
              showSearch
              style={{ minWidth: 280 }}
              size='small'
              placeholder={t('Buscar causa del proyecto')}
              value={selectedExistingCauseId ?? undefined}
              options={existingCauseOptions}
              loading={isLoadingProjectCauses}
              onChange={(value) => setSelectedExistingCauseId(value)}
              filterOption={(input, option) =>
                normalizeForSearch(String(option?.label ?? '')).includes(normalizeForSearch(input))
              }
            />
            <Button type='primary' size='small' onClick={handleLinkExistingCause}>
              {t('Enlazar')}
            </Button>
          </>
        )}
      </div>
      <Table
        columns={visibleColumns}
        dataSource={dataSourceWithSpan}
        bordered
        size='small'
        pagination={false}
        style={{ width: '100%' }}
        scroll={{ x: 'max-content' }}
        rowClassName={(record) => {
          const classes: string[] = [];
          if (record.isFirstForCause) {
            classes.push('deviation-cause-group-start');
          }
          if (record.isLastForCause) {
            classes.push('deviation-cause-group-end');
          }
          return classes.join(' ');
        }}
      />
    </div>
  );
};

export default DeviationTable;
